import React, { useState, useEffect, useMemo, useRef } from 'react';
import { format, startOfWeek, addDays, endOfWeek, parseISO } from 'date-fns';
import { shiftApi, shiftTemplateApi, scheduleApi, employeeApi, positionApi, employeeSortOrderApi, schedulePublicationStatusApi, payRecordApi, breakTimeSettingsApi } from '../services/api';
import { Shift, ShiftTemplate, Schedule, Employee, Position, PayRecord, BreakTimeSettings, BreakTimeRule } from '../types';
import { ChevronLeft, ChevronRight, Plus, Edit, Trash2, GripVertical, MapPin, X, Copy, Clipboard, MoreVertical, Lock, Unlock, CheckCircle, AlertCircle, Download, Calendar, ArrowDownToLine, Users, Clock, ChevronDown, Loader2, Calculator } from 'lucide-react';
import { useLocationContext } from './Layout';
import { useWeekStartDay } from '../hooks/useWeekStartDay';

const ScheduleCalendar: React.FC = () => {
  const weekStartsOn = useWeekStartDay(); // Universal week start day setting
  const { selectedLocationId, setSelectedLocationId, locations } = useLocationContext();
  
  // Load persisted week from localStorage
  const getInitialWeek = (): Date => {
    const savedWeek = localStorage.getItem('scheduling_currentWeek');
    if (savedWeek) {
      try {
        const parsedDate = new Date(savedWeek);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate;
        }
      } catch (e) {
        console.error('Error parsing saved week:', e);
      }
    }
    return new Date();
  };
  
  // Load persisted schedule ID from localStorage
  const getInitialScheduleId = (): number | null => {
    const savedScheduleId = localStorage.getItem('scheduling_selectedScheduleId');
    if (savedScheduleId) {
      try {
        const parsedId = Number(savedScheduleId);
        if (!isNaN(parsedId)) {
          return parsedId;
        }
      } catch (e) {
        console.error('Error parsing saved schedule ID:', e);
      }
    }
    return null;
  };
  
  const [currentWeek, setCurrentWeek] = useState<Date>(getInitialWeek());
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(getInitialScheduleId());
  const [draggedTemplate, setDraggedTemplate] = useState<ShiftTemplate | null>(null);
  const [dragOverCell, setDragOverCell] = useState<string | null>(null);
  const [editingShift, setEditingShift] = useState<Shift | null>(null);
  const [showShiftForm, setShowShiftForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    startTime: '',
    endTime: '',
    hours: 0,
    unpaidBreakMins: 0,
    breakTimePaid: false,
    totalHours: 0,
    color: '#3b82f6',
    displayOption: 'time' as 'time' | 'f' | 'close',
    positionId: undefined as number | undefined,
    notes: '',
    isManualColorOverride: false,
    originalColor: '#3b82f6', // Store original color for reset
  });

  // Break time settings state
  const [breakTimeSettings, setBreakTimeSettings] = useState<BreakTimeSettings | null>(null);
  const [breakTimeRules, setBreakTimeRules] = useState<BreakTimeRule[]>([]);
  const [breakMinsManuallySet, setBreakMinsManuallySet] = useState(false);

  // Memoize week calculations using universal week start day setting
  const weekStart = useMemo(() => startOfWeek(currentWeek, { weekStartsOn }), [currentWeek, weekStartsOn]);
  const weekEnd = useMemo(() => endOfWeek(currentWeek, { weekStartsOn }), [currentWeek, weekStartsOn]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  
  // Use ref to prevent duplicate location setting
  const hasSetLocationRef = useRef(false);

  // State for data
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [shiftTemplates, setShiftTemplates] = useState<ShiftTemplate[]>([]);
  const [positions, setPositions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [scheduleCounts, setScheduleCounts] = useState<Record<number, { totalShifts: number; uniqueEmployees: number; totalEmployeesAssigned: number }>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showSortModal, setShowSortModal] = useState(false);
  const [employeeSortOrder, setEmployeeSortOrder] = useState<{ employeeId: number; sortOrder: number }[]>([]);
  const [draggedEmployeeId, setDraggedEmployeeId] = useState<number | null>(null);
  const [dragOverEmployeeId, setDragOverEmployeeId] = useState<number | null>(null);
  const [hoveredCell, setHoveredCell] = useState<string | null>(null);
  const [hoveredShiftId, setHoveredShiftId] = useState<number | null>(null);
  const [copiedShift, setCopiedShift] = useState<Shift | null>(null);
  const [publicationStatus, setPublicationStatus] = useState<{ status: string; id?: number; publishedAt?: string; changedAt?: string } | null>(null);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showUnlockWarning, setShowUnlockWarning] = useState(false);
  const [pendingEditAction, setPendingEditAction] = useState<(() => void) | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [totalDisplayMode, setTotalDisplayMode] = useState<'hours' | 'cost'>('hours');
  const [showTotalDropdown, setShowTotalDropdown] = useState(false);
  const [employeePayRecords, setEmployeePayRecords] = useState<Record<number, PayRecord>>({});
  const totalHeaderDropdownRef = useRef<HTMLDivElement>(null);
  const [showCostBreakdownModal, setShowCostBreakdownModal] = useState(false);
  const [selectedEmployeeForBreakdown, setSelectedEmployeeForBreakdown] = useState<Employee | null>(null);
  const [savingShift, setSavingShift] = useState(false);
  const [creatingShift, setCreatingShift] = useState(false);
  const [loadingShifts, setLoadingShifts] = useState(false);
  const [showScheduleBreakdownModal, setShowScheduleBreakdownModal] = useState(false);
  const [locationEmployees, setLocationEmployees] = useState<Employee[]>([]);
  const [locationShifts, setLocationShifts] = useState<Shift[]>([]);
  const [locationPayRecords, setLocationPayRecords] = useState<Record<number, PayRecord>>({});
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (totalHeaderDropdownRef.current && !totalHeaderDropdownRef.current.contains(event.target as Node)) {
        setShowTotalDropdown(false);
      }
    };

    if (showTotalDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTotalDropdown]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importWeekStart, setImportWeekStart] = useState<Date | null>(null);
  const [importShifts, setImportShifts] = useState<Shift[]>([]);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [colorMode, setColorMode] = useState<'shift' | 'position'>('shift');
  const [showPositionDropdown, setShowPositionDropdown] = useState(false);

  // Set first location as default if none selected (only once)
  useEffect(() => {
    if (locations.length > 0 && !selectedLocationId && !hasSetLocationRef.current) {
      setSelectedLocationId(locations[0].id);
      hasSetLocationRef.current = true;
    }
  }, [locations, selectedLocationId, setSelectedLocationId]);

  // Persist current week to localStorage
  useEffect(() => {
    localStorage.setItem('scheduling_currentWeek', currentWeek.toISOString());
  }, [currentWeek]);

  // Persist selected schedule ID to localStorage
  useEffect(() => {
    if (selectedScheduleId !== null) {
      localStorage.setItem('scheduling_selectedScheduleId', selectedScheduleId.toString());
    }
  }, [selectedScheduleId]);

  // Close position dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showPositionDropdown && !target.closest('.position-dropdown-container')) {
        setShowPositionDropdown(false);
      }
    };

    if (showPositionDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPositionDropdown]);

  // Fetch schedules (only once on mount)
  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        const schedulesData = await scheduleApi.getAll();
        setSchedules(schedulesData);
      } catch (error) {
        console.error('Error fetching schedules:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSchedules();
  }, []);

  // Fetch break time settings (only once on mount)
  useEffect(() => {
    const fetchBreakTimeSettings = async () => {
      try {
        const breakTimeData = await breakTimeSettingsApi.get().catch(() => ({ id: undefined, isPaid: false, rules: [] }));
        setBreakTimeSettings(breakTimeData || { id: undefined, isPaid: false, rules: [] });
        if (breakTimeData?.rules && Array.isArray(breakTimeData.rules) && breakTimeData.rules.length > 0) {
          setBreakTimeRules(breakTimeData.rules);
        } else {
          setBreakTimeRules([
            { minHours: 0, maxHours: 4.5, breakMinutes: 0 },
            { minHours: 4.5, maxHours: 6.5, breakMinutes: 15 },
            { minHours: 6.5, maxHours: 10, breakMinutes: 30 },
            { minHours: 10, maxHours: 24, breakMinutes: 45 },
          ]);
        }
      } catch (error) {
        console.error('Error fetching break time settings:', error);
        setBreakTimeRules([
          { minHours: 0, maxHours: 4.5, breakMinutes: 0 },
          { minHours: 4.5, maxHours: 6.5, breakMinutes: 15 },
          { minHours: 6.5, maxHours: 10, breakMinutes: 30 },
          { minHours: 10, maxHours: 24, breakMinutes: 45 },
        ]);
      }
    };
    fetchBreakTimeSettings();
  }, []);

  // Fetch employees when location and schedule are selected
  useEffect(() => {
    if (selectedLocationId && selectedScheduleId) {
      const fetchEmployees = async () => {
        try {
          const employeesData = await employeeApi.getAll();
          setEmployees(employeesData || []);
        } catch (error) {
          console.error('Error fetching employees:', error);
          setEmployees([]);
        }
      };
      fetchEmployees();
    }
  }, [selectedLocationId, selectedScheduleId]);

  // Fetch shift counts for all schedules
  const fetchShiftCounts = async () => {
    if (!selectedLocationId) {
      setScheduleCounts({});
      return;
    }
    
    try {
      const countsData = await shiftApi.getCounts({
        startDate: format(weekStart, 'yyyy-MM-dd'),
        endDate: format(weekEnd, 'yyyy-MM-dd'),
        locationId: selectedLocationId,
      });
      
      // Convert array to object keyed by scheduleId
      const countsMap: Record<number, { totalShifts: number; uniqueEmployees: number; totalEmployeesAssigned: number }> = {};
      countsData.forEach(item => {
        countsMap[item.scheduleId] = {
          totalShifts: item.totalShifts,
          uniqueEmployees: item.uniqueEmployees,
          totalEmployeesAssigned: item.totalEmployeesAssigned || 0
        };
      });
      setScheduleCounts(countsMap);
    } catch (error) {
      console.error('Error fetching shift counts:', error);
      setScheduleCounts({});
    }
  };

  // Fetch shift counts when location or week changes
  useEffect(() => {
    fetchShiftCounts();
  }, [selectedLocationId, weekStart, weekEnd]);

  // Fetch PayRecords for employees - single consolidated call instead of N per-employee calls
  useEffect(() => {
    const fetchPayRecords = async () => {
      if (employees.length === 0) return;

      try {
        const allRecords = await payRecordApi.getAll();
        const payRecordsMap: Record<number, PayRecord> = {};
        for (const employee of employees) {
          const records = allRecords.filter(r => r.employeeId === employee.id);
          const activeRecord = records.find(r => r.isActive) || records[records.length - 1];
          if (activeRecord) payRecordsMap[employee.id] = activeRecord;
        }
        setEmployeePayRecords(payRecordsMap);
      } catch (error) {
        console.error('Error fetching pay records:', error);
      }
    };

    fetchPayRecords();
  }, [employees]);

  // Fetch breakdown data when schedule breakdown modal opens - consolidated: 1 employees + 1 shifts + 1 pay-records
  useEffect(() => {
    const fetchBreakdownData = async () => {
      if (!showScheduleBreakdownModal || !selectedLocationId) return;

      setLoadingBreakdown(true);
      try {
        const weekStartStr = format(weekStart, 'yyyy-MM-dd');
        const weekEndStr = format(weekEnd, 'yyyy-MM-dd');

        const [allEmployees, allShiftsForLocation, allPayRecords] = await Promise.all([
          employeeApi.getAll(),
          shiftApi.getAll({ startDate: weekStartStr, endDate: weekEndStr, locationId: selectedLocationId }),
          payRecordApi.getAll(),
        ]);

        const locationEmps = allEmployees.filter(e => {
          const hasMainLocation = e.profile?.mainLocationId === selectedLocationId;
          const hasSubLocation = e.profile?.subLocationIds &&
            Array.isArray(e.profile.subLocationIds) &&
            e.profile.subLocationIds.includes(selectedLocationId);
          return hasMainLocation || hasSubLocation;
        });
        setLocationEmployees(locationEmps);
        setLocationShifts(allShiftsForLocation);

        const payRecordMap: Record<number, PayRecord> = {};
        for (const employee of locationEmps) {
          const records = allPayRecords.filter(r => r.employeeId === employee.id);
          const activeRecord = records.find(r => r.isActive) || records[records.length - 1];
          if (activeRecord) payRecordMap[employee.id] = activeRecord;
        }
        setLocationPayRecords(payRecordMap);
      } catch (error) {
        console.error('Error fetching breakdown data:', error);
      } finally {
        setLoadingBreakdown(false);
      }
    };

    fetchBreakdownData();
  }, [showScheduleBreakdownModal, selectedLocationId, weekStart, weekEnd]);

  // Fetch shifts when location, schedule, or week changes
  // Optimized: single API call filtered by location + schedule instead of N+1 location calls
  useEffect(() => {
    if (selectedLocationId && selectedScheduleId && employees.length > 0) {
      const fetchShifts = async () => {
        setLoadingShifts(true);
        try {
          const weekStartStr = format(weekStart, 'yyyy-MM-dd');
          const weekEndStr = format(weekEnd, 'yyyy-MM-dd');

          // Fetch only shifts for the selected location and schedule for this week
          const locationShifts = await shiftApi.getAll({
            startDate: weekStartStr,
            endDate: weekEndStr,
            locationId: selectedLocationId,
            scheduleId: selectedScheduleId,
          });

          setShifts(locationShifts);
        } catch (error) {
          console.error('Error fetching shifts:', error);
          setShifts([]);
        } finally {
          setLoadingShifts(false);
        }
      };
      fetchShifts();
    } else {
      setShifts([]);
      setLoadingShifts(false);
    }
  }, [selectedLocationId, selectedScheduleId, weekStart, weekEnd, locations, employees]);

  // Fetch templates when location changes
  useEffect(() => {
    if (selectedLocationId) {
      const fetchTemplates = async () => {
        try {
          const templatesData = await shiftTemplateApi.getAll(selectedLocationId);
          setShiftTemplates(templatesData);
        } catch (error) {
          console.error('Error fetching shift templates:', error);
        }
      };
      fetchTemplates();
    }
  }, [selectedLocationId]);

  // Fetch positions
  useEffect(() => {
    const fetchPositions = async () => {
      try {
        const positionsData = await positionApi.getAll();
        setPositions(positionsData);
      } catch (error) {
        console.error('Error fetching positions:', error);
      }
    };
    fetchPositions();
  }, []);

  // Load sort order when location, schedule, or week changes
  useEffect(() => {
    if (selectedLocationId && selectedScheduleId) {
      const loadSortOrder = async () => {
        try {
          const weekStartStr = format(weekStart, 'yyyy-MM-dd');
          const sortOrder = await employeeSortOrderApi.get(selectedLocationId, selectedScheduleId, weekStartStr);
          setEmployeeSortOrder(sortOrder);
        } catch (error) {
          setEmployeeSortOrder([]);
        }
      };
      loadSortOrder();
    }
  }, [selectedLocationId, selectedScheduleId, weekStart]);

  // Load publication status when location, schedule, or week changes
  useEffect(() => {
    if (selectedLocationId && selectedScheduleId) {
      const loadPublicationStatus = async () => {
        try {
          const weekStartStr = format(weekStart, 'yyyy-MM-dd');
          const status = await schedulePublicationStatusApi.get(selectedLocationId, selectedScheduleId, weekStartStr);
          setPublicationStatus(status);
        } catch (error) {
          console.error('Error fetching publication status:', error);
          setPublicationStatus({ status: 'Unpublished' });
        }
      };
      loadPublicationStatus();
    } else {
      setPublicationStatus(null);
    }
  }, [selectedLocationId, selectedScheduleId, weekStart]);

  useEffect(() => {
    if (schedules.length > 0 && selectedScheduleId === null) {
      // Check if saved schedule ID is still valid
      const savedScheduleId = localStorage.getItem('scheduling_selectedScheduleId');
      if (savedScheduleId) {
        const parsedId = Number(savedScheduleId);
        if (!isNaN(parsedId) && schedules.some(s => s.id === parsedId)) {
          setSelectedScheduleId(parsedId);
          return;
        }
      }
      // If no valid saved schedule, use first one
      setSelectedScheduleId(schedules[0].id);
    }
  }, [schedules, selectedScheduleId]);

  // Filter employees by location and schedule assignment
  // Show employees who have the selected location (main or sub) AND the selected schedule
  const filteredEmployees = useMemo(() => {
    if (!selectedLocationId || !selectedScheduleId) return [];
    
    let filtered = employees.filter(emp => {
      // Check if employee has the selected location (main location OR sub location)
      const hasMainLocation = emp.profile?.mainLocationId === selectedLocationId;
      const hasSubLocation = emp.profile?.subLocationIds && 
                            Array.isArray(emp.profile.subLocationIds) && 
                            emp.profile.subLocationIds.includes(selectedLocationId);
      const hasLocation = hasMainLocation || hasSubLocation;
      
      // Check if employee has the selected schedule
      const hasSchedule = emp.profile?.scheduleIds && 
                         Array.isArray(emp.profile.scheduleIds) && 
                         emp.profile.scheduleIds.includes(selectedScheduleId);
      
      // Employee must have BOTH the location AND the schedule
      return hasLocation && hasSchedule;
    });
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(emp => 
        `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(query) ||
        emp.email.toLowerCase().includes(query)
      );
    }

    // Apply sort order if available
    if (employeeSortOrder.length > 0) {
      filtered = filtered.sort((a, b) => {
        const orderA = employeeSortOrder.find(o => o.employeeId === a.id)?.sortOrder ?? 999;
        const orderB = employeeSortOrder.find(o => o.employeeId === b.id)?.sortOrder ?? 999;
        return orderA - orderB;
      });
    }

    return filtered;
  }, [employees, selectedLocationId, selectedScheduleId, searchQuery, employeeSortOrder]);

  // Parse datetime string as local time (no timezone conversion)
  const parseLocalDateTime = (dateTimeStr: string): Date => {
    // If the string doesn't have timezone info, treat it as local time
    if (!dateTimeStr.includes('Z') && !dateTimeStr.includes('+') && !dateTimeStr.includes('-', 10)) {
      // Format: "yyyy-MM-ddTHH:mm:ss" - parse as local time
      const [datePart, timePart] = dateTimeStr.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute, second] = (timePart || '00:00:00').split(':').map(Number);
      return new Date(year, month - 1, day, hour, minute, second || 0);
    }
    // If it has timezone info, use parseISO but adjust to local
    return parseISO(dateTimeStr);
  };

  // Get shifts for employee and date, sorted by start time
  const getShiftsForEmployeeAndDate = (employeeId: number, date: Date): Shift[] => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const filteredShifts = shifts.filter(shift => {
      const shiftDate = format(parseLocalDateTime(shift.startTime), 'yyyy-MM-dd');
      const matches = shift.employeeId === employeeId && shiftDate === dateStr;
      return matches;
    });
    
    // Sort by start time
    return filteredShifts.sort((a, b) => {
      const timeA = parseLocalDateTime(a.startTime).getTime();
      const timeB = parseLocalDateTime(b.startTime).getTime();
      return timeA - timeB;
    });
  };

  // Check if a shift time overlaps with existing shifts (checks ALL shifts for employee across all schedules/locations)
  const checkTimeOverlap = async (employeeId: number, date: Date, newStartTime: string, newEndTime: string, excludeShiftId?: number): Promise<boolean> => {
    try {
      // Fetch ALL shifts for this employee on this date from API (across all schedules and locations)
      // This ensures we check against the latest data, not just cached shifts
      const dateStr = format(date, 'yyyy-MM-dd');
      const weekStartDate = startOfWeek(date, { weekStartsOn });
      const weekEndDate = endOfWeek(date, { weekStartsOn });
      
      // Fetch all shifts for this employee in the week containing this date
      const allEmployeeShifts = await shiftApi.getAll({
        startDate: format(weekStartDate, 'yyyy-MM-dd'),
        endDate: format(weekEndDate, 'yyyy-MM-dd'),
        employeeId: employeeId,
      });
      
      // Filter to shifts on the specific date
      const shiftsOnDate = allEmployeeShifts.filter(shift => {
        const shiftDate = format(parseLocalDateTime(shift.startTime), 'yyyy-MM-dd');
        return shiftDate === dateStr;
      });
      
      const newStart = parseLocalDateTime(newStartTime);
      const newEnd = parseLocalDateTime(newEndTime);
      
      return shiftsOnDate.some(existingShift => {
        // Skip the shift being edited
        if (excludeShiftId && existingShift.id === excludeShiftId) {
          return false;
        }
        
        const existingStart = parseLocalDateTime(existingShift.startTime);
        const existingEnd = parseLocalDateTime(existingShift.endTime);
        
        // Check if times overlap: (newStart < existingEnd && newEnd > existingStart)
        return (newStart < existingEnd && newEnd > existingStart);
      });
    } catch (error) {
      console.error('Error checking overlap:', error);
      // Fallback to checking cached shifts if API call fails
      const dateStr = format(date, 'yyyy-MM-dd');
      const allEmployeeShifts = shifts.filter(shift => {
        const shiftDate = format(parseLocalDateTime(shift.startTime), 'yyyy-MM-dd');
        return shift.employeeId === employeeId && shiftDate === dateStr;
      });
      
      const newStart = parseLocalDateTime(newStartTime);
      const newEnd = parseLocalDateTime(newEndTime);
      
      return allEmployeeShifts.some(existingShift => {
        if (excludeShiftId && existingShift.id === excludeShiftId) {
          return false;
        }
        
        const existingStart = parseLocalDateTime(existingShift.startTime);
        const existingEnd = parseLocalDateTime(existingShift.endTime);
        
        return (newStart < existingEnd && newEnd > existingStart);
      });
    }
  };

  // Calculate total hours for a shift (paid hours + unpaid hours)
  const getShiftTotalHours = (shift: Shift): number => {
    const paidHours = shift.paidHours || 0;
    const unpaidHours = shift.unpaidHours || 0;
    return Number(paidHours) + Number(unpaidHours);
  };

  // Calculate paid hours for a shift (for cost calculation)
  // If break time is unpaid, deduct it from total hours
  const getShiftPaidHoursForCost = (shift: Shift): number => {
    const paidHours = shift.paidHours || 0;
    const unpaidHours = shift.unpaidHours || 0;
    const breakTimePaid = shift.breakTimePaid ?? false;
    
    // If break time is unpaid, only return paid hours (break already deducted)
    // If break time is paid, return total hours (paidHours + unpaidHours)
    if (!breakTimePaid) {
      return Number(paidHours);
    } else {
      return Number(paidHours) + Number(unpaidHours);
    }
  };

  // Calculate total hours for a specific day (across all employees) - CURRENT SCHEDULE ONLY
  const getDayTotalHours = (date: Date): number => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return filteredEmployees.reduce((total, employee) => {
      const employeeShifts = getShiftsForEmployeeAndDate(employee.id, date);
      // Filter to only current schedule shifts
      const currentScheduleShifts = employeeShifts.filter(shift => 
        shift.scheduleId === selectedScheduleId && shift.locationId === selectedLocationId
      );
      const employeeDayTotal = currentScheduleShifts.reduce((sum, shift) => sum + getShiftTotalHours(shift), 0);
      return total + employeeDayTotal;
    }, 0);
  };

  // Calculate total hours for a specific day across ALL schedules (same location)
  const getDayTotalHoursAllSchedules = (date: Date): number => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return filteredEmployees.reduce((total, employee) => {
      const employeeShifts = getShiftsForEmployeeAndDate(employee.id, date);
      // Filter to only shifts from selected location (all schedules)
      const locationShifts = employeeShifts.filter(shift => shift.locationId === selectedLocationId);
      const employeeDayTotal = locationShifts.reduce((sum, shift) => sum + getShiftTotalHours(shift), 0);
      return total + employeeDayTotal;
    }, 0);
  };

  // Calculate total hours for an employee (across all days in the week) - CURRENT SCHEDULE ONLY
  const getEmployeeTotalHours = (employeeId: number): number => {
    return weekDays.reduce((total, day) => {
      const employeeShifts = getShiftsForEmployeeAndDate(employeeId, day);
      // Filter to only current schedule shifts
      const currentScheduleShifts = employeeShifts.filter(shift => 
        shift.scheduleId === selectedScheduleId && shift.locationId === selectedLocationId
      );
      const dayTotal = currentScheduleShifts.reduce((sum, shift) => sum + getShiftTotalHours(shift), 0);
      return total + dayTotal;
    }, 0);
  };

  // Calculate total hours for an employee across ALL schedules (same location only)
  const getEmployeeTotalHoursAllSchedules = (employeeId: number): number => {
    return weekDays.reduce((total, day) => {
      const employeeShifts = getShiftsForEmployeeAndDate(employeeId, day);
      // Filter to only shifts from selected location (all schedules)
      const locationShifts = employeeShifts.filter(shift => shift.locationId === selectedLocationId);
      const dayTotal = locationShifts.reduce((sum, shift) => sum + getShiftTotalHours(shift), 0);
      return total + dayTotal;
    }, 0);
  };

  // Calculate grand total (all employees, all days) - CURRENT SCHEDULE ONLY
  const getGrandTotalHours = (): number => {
    return filteredEmployees.reduce((total, employee) => {
      return total + getEmployeeTotalHours(employee.id);
    }, 0);
  };

  // Calculate grand total hours across ALL schedules (same location)
  const getGrandTotalHoursAllSchedules = (): number => {
    return filteredEmployees.reduce((total, employee) => {
      return total + getEmployeeTotalHoursAllSchedules(employee.id);
    }, 0);
  };

  // Calculate hourly rate from PayRecord based on payment type
  const calculateHourlyRateFromPayRecord = (payRecord: PayRecord | undefined): number => {
    if (!payRecord) return 0;
    
    const paymentTypeName = payRecord.paymentType?.name || '';
    const baseRate = payRecord.baseRate || 0;
    
    // For Hourly payment type, BaseRate is already the hourly rate
    if (paymentTypeName === 'Hourly') {
      return baseRate;
    }
    
    // For non-hourly types, MUST use AssignLaborCostBy option - no fallbacks
    const assignLaborCostByName = payRecord.assignLaborCostBy?.name || '';
    const contractedHours = payRecord.contractedHours || 0;
    const workingDaysPerWeek = payRecord.workingDaysPerWeek || 0;
    const weeklyRate = payRecord.weeklyRate || 0;
    const dailyRate = payRecord.dailyRate || 0;
    const presetDaysCount = payRecord.payRecordWorkingDays?.length || 0;
    
    // Get payment frequency to determine if baseRate is weekly or monthly
    const paymentFrequencyName = payRecord.paymentFrequency?.name || '';
    const isMonthlyFrequency = paymentFrequencyName === 'Monthly';
    
    // For Project type, calculate weeks from ProjectStartDate to ProjectEndDate
    let projectWeeks = 0;
    if (paymentTypeName === 'Project' && payRecord.projectStartDate && payRecord.projectEndDate) {
      const startDate = new Date(payRecord.projectStartDate);
      const endDate = new Date(payRecord.projectEndDate);
      const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      projectWeeks = Math.max(daysDiff / 7, 1); // At least 1 week
    }
    
    // For non-hourly payment types, MUST use AssignLaborCostBy option
    // If not set, return 0 (cost allocation option is required)
    if (!assignLaborCostByName) {
      return 0;
    }
    
    // Calculate hourly rate based on AssignLaborCostBy option
    if (assignLaborCostByName === 'Allocated Hours' && contractedHours > 0) {
      // BaseRate is weekly/monthly rate, convert to hourly
      if (isMonthlyFrequency) {
        // Monthly: BaseRate (monthly salary) ÷ (ContractedHours per week × 4.33 weeks per month)
        const monthlyHours = contractedHours * 4.33;
        return monthlyHours > 0 ? baseRate / monthlyHours : 0;
      } else {
        // Weekly: BaseRate / ContractedHours per week
        return baseRate / contractedHours;
      }
    } else if (assignLaborCostByName === 'Daily Rate' && workingDaysPerWeek > 0) {
      // If DailyRate is set, use it: (DailyRate * WorkingDaysPerWeek) / ContractedHours
      if (dailyRate > 0 && contractedHours > 0) {
        const weeklyTotal = dailyRate * workingDaysPerWeek;
        return weeklyTotal / contractedHours;
      }
      // If DailyRate not set but Daily Rate option is selected, cannot calculate
      return 0;
    } else if (assignLaborCostByName === 'Pre Set Days' && presetDaysCount > 0) {
      // BaseRate is for preset days, calculate hourly: BaseRate / (ContractedHours * PresetDaysCount / 5)
      // Assuming standard 5-day work week
      if (contractedHours > 0) {
        if (isMonthlyFrequency) {
          // Monthly: convert to monthly hours first
          const monthlyHours = contractedHours * 4.33 * (presetDaysCount / 5);
          return monthlyHours > 0 ? baseRate / monthlyHours : 0;
        } else {
          const adjustedHours = contractedHours * (presetDaysCount / 5);
          return adjustedHours > 0 ? baseRate / adjustedHours : 0;
        }
      }
      return 0;
    } else if (assignLaborCostByName === 'Do not Allocate') {
      // Do not allocate labor cost - return 0
      return 0;
    }
    
    // If AssignLaborCostBy is set but doesn't match any known option, return 0
    return 0;
  };

  // Calculate labor cost for a shift
  const getShiftLaborCost = (shift: Shift): number => {
    const payRecord = employeePayRecords[shift.employeeId];
    if (!payRecord) return 0;
    
    const hourlyRate = calculateHourlyRateFromPayRecord(payRecord);
    const shiftHours = getShiftPaidHoursForCost(shift);
    
    return hourlyRate * shiftHours;
  };

  // Calculate total cost for a specific day (across all employees) - CURRENT SCHEDULE ONLY
  const getDayTotalCost = (date: Date): number => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return filteredEmployees.reduce((total, employee) => {
      const employeeShifts = getShiftsForEmployeeAndDate(employee.id, date);
      // Filter to only current schedule shifts
      const currentScheduleShifts = employeeShifts.filter(shift => 
        shift.scheduleId === selectedScheduleId && shift.locationId === selectedLocationId
      );
      const employeeDayTotal = currentScheduleShifts.reduce((sum, shift) => sum + getShiftLaborCost(shift), 0);
      return total + employeeDayTotal;
    }, 0);
  };

  // Calculate total cost for a specific day across ALL schedules (same location)
  const getDayTotalCostAllSchedules = (date: Date): number => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return filteredEmployees.reduce((total, employee) => {
      const employeeShifts = getShiftsForEmployeeAndDate(employee.id, date);
      // Filter to only shifts from selected location (all schedules)
      const locationShifts = employeeShifts.filter(shift => shift.locationId === selectedLocationId);
      const employeeDayTotal = locationShifts.reduce((sum, shift) => sum + getShiftLaborCost(shift), 0);
      return total + employeeDayTotal;
    }, 0);
  };

  // Calculate total cost for an employee (across all days in the week) - CURRENT SCHEDULE ONLY
  const getEmployeeTotalCost = (employeeId: number): number => {
    return weekDays.reduce((total, day) => {
      const employeeShifts = getShiftsForEmployeeAndDate(employeeId, day);
      // Filter to only current schedule shifts
      const currentScheduleShifts = employeeShifts.filter(shift => 
        shift.scheduleId === selectedScheduleId && shift.locationId === selectedLocationId
      );
      const dayTotal = currentScheduleShifts.reduce((sum, shift) => sum + getShiftLaborCost(shift), 0);
      return total + dayTotal;
    }, 0);
  };

  // Calculate total cost for an employee across ALL schedules (same location only)
  const getEmployeeTotalCostAllSchedules = (employeeId: number): number => {
    return weekDays.reduce((total, day) => {
      const employeeShifts = getShiftsForEmployeeAndDate(employeeId, day);
      // Filter to only shifts from selected location (all schedules)
      const locationShifts = employeeShifts.filter(shift => shift.locationId === selectedLocationId);
      const dayTotal = locationShifts.reduce((sum, shift) => sum + getShiftLaborCost(shift), 0);
      return total + dayTotal;
    }, 0);
  };

  // Calculate grand total cost (all employees, all days) - CURRENT SCHEDULE ONLY
  const getGrandTotalCost = (): number => {
    return filteredEmployees.reduce((total, employee) => {
      return total + getEmployeeTotalCost(employee.id);
    }, 0);
  };

  // Calculate grand total cost across ALL schedules (same location)
  const getGrandTotalCostAllSchedules = (): number => {
    return filteredEmployees.reduce((total, employee) => {
      return total + getEmployeeTotalCostAllSchedules(employee.id);
    }, 0);
  };

  // Memoized totals so opening the Total dropdown doesn't trigger expensive recalc (only recalc when data changes).
  // When loadingShifts (e.g. after week nav), return lightweight zero cache so render is instant.
  const totalsCache = useMemo(() => {
    if (loadingShifts) {
      const empH = {} as Record<number, number>;
      const empC = {} as Record<number, number>;
      const empHA = {} as Record<number, number>;
      const empCA = {} as Record<number, number>;
      for (const emp of filteredEmployees) {
        empH[emp.id] = 0;
        empC[emp.id] = 0;
        empHA[emp.id] = 0;
        empCA[emp.id] = 0;
      }
      const dayH = {} as Record<string, number>;
      const dayC = {} as Record<string, number>;
      const dayHA = {} as Record<string, number>;
      const dayCA = {} as Record<string, number>;
      for (const day of weekDays) {
        const d = format(day, 'yyyy-MM-dd');
        dayH[d] = 0;
        dayC[d] = 0;
        dayHA[d] = 0;
        dayCA[d] = 0;
      }
      return {
        employeeHoursCurrent: empH,
        employeeCostCurrent: empC,
        employeeHoursAll: empHA,
        employeeCostAll: empCA,
        dayHoursCurrent: dayH,
        dayCostCurrent: dayC,
        dayHoursAll: dayHA,
        dayCostAll: dayCA,
        grandHoursCurrent: 0,
        grandCostCurrent: 0,
        grandHoursAll: 0,
        grandCostAll: 0,
      };
    }
    const employeeHoursCurrent: Record<number, number> = {};
    const employeeCostCurrent: Record<number, number> = {};
    const employeeHoursAll: Record<number, number> = {};
    const employeeCostAll: Record<number, number> = {};
    for (const emp of filteredEmployees) {
      employeeHoursCurrent[emp.id] = getEmployeeTotalHours(emp.id);
      employeeCostCurrent[emp.id] = getEmployeeTotalCost(emp.id);
      employeeHoursAll[emp.id] = getEmployeeTotalHoursAllSchedules(emp.id);
      employeeCostAll[emp.id] = getEmployeeTotalCostAllSchedules(emp.id);
    }
    const dayHoursCurrent: Record<string, number> = {};
    const dayCostCurrent: Record<string, number> = {};
    const dayHoursAll: Record<string, number> = {};
    const dayCostAll: Record<string, number> = {};
    for (const day of weekDays) {
      const dayStr = format(day, 'yyyy-MM-dd');
      dayHoursCurrent[dayStr] = getDayTotalHours(day);
      dayCostCurrent[dayStr] = getDayTotalCost(day);
      dayHoursAll[dayStr] = getDayTotalHoursAllSchedules(day);
      dayCostAll[dayStr] = getDayTotalCostAllSchedules(day);
    }
    return {
      employeeHoursCurrent,
      employeeCostCurrent,
      employeeHoursAll,
      employeeCostAll,
      dayHoursCurrent,
      dayCostCurrent,
      dayHoursAll,
      dayCostAll,
      grandHoursCurrent: getGrandTotalHours(),
      grandCostCurrent: getGrandTotalCost(),
      grandHoursAll: getGrandTotalHoursAllSchedules(),
      grandCostAll: getGrandTotalCostAllSchedules(),
    };
  }, [shifts, employeePayRecords, weekDays, filteredEmployees, selectedScheduleId, selectedLocationId, loadingShifts]);

  // Get formula explanation for PayRecord
  const getFormulaExplanation = (payRecord: PayRecord): { formula: string; description: string; values: Record<string, any> } => {
    const paymentTypeName = payRecord.paymentType?.name || '';
    const baseRate = payRecord.baseRate || 0;
    const assignLaborCostByName = payRecord.assignLaborCostBy?.name || '';
    const contractedHours = payRecord.contractedHours || 0;
    const workingDaysPerWeek = payRecord.workingDaysPerWeek || 0;
    const weeklyRate = payRecord.weeklyRate || 0;
    const dailyRate = payRecord.dailyRate || 0;
    const presetDaysCount = payRecord.payRecordWorkingDays?.length || 0;
    const paymentFrequencyName = payRecord.paymentFrequency?.name || '';
    const isMonthlyFrequency = paymentFrequencyName === 'Monthly';
    
    let projectWeeks = 0;
    if (paymentTypeName === 'Project' && payRecord.projectStartDate && payRecord.projectEndDate) {
      const startDate = new Date(payRecord.projectStartDate);
      const endDate = new Date(payRecord.projectEndDate);
      const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      projectWeeks = Math.max(daysDiff / 7, 1);
    }

    if (paymentTypeName === 'Hourly') {
      return {
        formula: 'Hourly Rate = Base Rate',
        description: 'For Hourly payment type, Base Rate is already the hourly rate.',
        values: {
          'Base Rate': `€${baseRate.toFixed(2)}`,
          'Hourly Rate': `€${baseRate.toFixed(2)}`,
        }
      };
    }
    
    // For non-hourly types, MUST use AssignLaborCostBy option
    if (!assignLaborCostByName) {
      return {
        formula: 'Cost Allocation Not Set',
        description: 'For non-hourly payment types, Assign Labor Cost By option must be set to calculate hourly rate.',
        values: {
          'Payment Type': paymentTypeName,
          'Assign Labor Cost By': 'Not Set',
        }
      };
    }
    
    if (assignLaborCostByName === 'Allocated Hours' && contractedHours > 0) {
      let hourlyRate: number;
      let formula: string;
      let description: string;
      let values: Record<string, any>;
      
      if (isMonthlyFrequency) {
        const monthlyHours = contractedHours * 4.33;
        hourlyRate = monthlyHours > 0 ? baseRate / monthlyHours : 0;
        formula = 'Hourly Rate = Monthly Salary ÷ (Contracted Hours per Week × 4.33 weeks per month)';
        description = 'Base Rate is monthly salary. Convert to monthly hours (weekly hours × 4.33) then divide to get hourly rate.';
        values = {
          'Monthly Salary (Base Rate)': `€${baseRate.toFixed(2)}`,
          'Contracted Hours per Week': `${contractedHours.toFixed(2)}`,
          'Weeks per Month': '4.33',
          'Monthly Hours': `${monthlyHours.toFixed(2)}`,
          'Hourly Rate': `€${hourlyRate.toFixed(2)}`,
        };
      } else {
        hourlyRate = baseRate / contractedHours;
        formula = 'Hourly Rate = Base Rate ÷ Contracted Hours per Week';
        description = 'Base Rate is the weekly rate. Divide by contracted hours per week to get hourly rate.';
        values = {
          'Base Rate': `€${baseRate.toFixed(2)}`,
          'Contracted Hours per Week': `${contractedHours.toFixed(2)}`,
          'Hourly Rate': `€${hourlyRate.toFixed(2)}`,
        };
      }
      
      return { formula, description, values };
    } else if (assignLaborCostByName === 'Daily Rate' && workingDaysPerWeek > 0) {
      if (dailyRate > 0 && contractedHours > 0) {
        const weeklyTotal = dailyRate * workingDaysPerWeek;
        const hourlyRate = weeklyTotal / contractedHours;
        return {
          formula: 'Hourly Rate = (Daily Rate × Working Days per Week) ÷ Contracted Hours per Week',
          description: 'Calculate weekly total from daily rate, then divide by contracted hours.',
          values: {
            'Daily Rate': `€${dailyRate.toFixed(2)}`,
            'Working Days per Week': `${workingDaysPerWeek}`,
            'Weekly Total': `€${weeklyTotal.toFixed(2)}`,
            'Contracted Hours per Week': `${contractedHours.toFixed(2)}`,
            'Hourly Rate': `€${hourlyRate.toFixed(2)}`,
          }
        };
      } else {
        // Daily Rate option selected but dailyRate not set
        return {
          formula: 'Daily Rate Not Set',
          description: 'Assign Labor Cost By is set to "Daily Rate" but Daily Rate value is not provided.',
          values: {
            'Assign Labor Cost By': 'Daily Rate',
            'Daily Rate': 'Not Set',
          }
        };
      }
    } else if (assignLaborCostByName === 'Pre Set Days' && presetDaysCount > 0 && contractedHours > 0) {
      let hourlyRate: number;
      let formula: string;
      let description: string;
      let values: Record<string, any>;
      
      if (isMonthlyFrequency) {
        const monthlyHours = contractedHours * 4.33 * (presetDaysCount / 5);
        hourlyRate = monthlyHours > 0 ? baseRate / monthlyHours : 0;
        formula = 'Hourly Rate = Monthly Salary ÷ (Contracted Hours per Week × 4.33 × Preset Days ÷ 5)';
        description = 'Base Rate is monthly salary for preset days. Convert to monthly hours adjusted for preset days ratio.';
        values = {
          'Monthly Salary (Base Rate)': `€${baseRate.toFixed(2)}`,
          'Contracted Hours per Week': `${contractedHours.toFixed(2)}`,
          'Preset Days Count': `${presetDaysCount}`,
          'Weeks per Month': '4.33',
          'Adjusted Monthly Hours': `${monthlyHours.toFixed(2)}`,
          'Hourly Rate': `€${hourlyRate.toFixed(2)}`,
        };
      } else {
        const adjustedHours = contractedHours * (presetDaysCount / 5);
        hourlyRate = adjustedHours > 0 ? baseRate / adjustedHours : 0;
        formula = 'Hourly Rate = Base Rate ÷ (Contracted Hours × Preset Days ÷ 5)';
        description = 'Base Rate is for preset days. Adjust contracted hours based on preset days ratio (assuming 5-day work week).';
        values = {
          'Base Rate': `€${baseRate.toFixed(2)}`,
          'Contracted Hours per Week': `${contractedHours.toFixed(2)}`,
          'Preset Days Count': `${presetDaysCount}`,
          'Adjusted Hours': `${adjustedHours.toFixed(2)}`,
          'Hourly Rate': `€${hourlyRate.toFixed(2)}`,
        };
      }
      
      return { formula, description, values };
    } else if (assignLaborCostByName === 'Do not Allocate') {
      return {
        formula: 'No Cost Allocation',
        description: 'Assign Labor Cost By is set to "Do not Allocate". Labor cost will not be calculated.',
        values: {
          'Assign Labor Cost By': 'Do not Allocate',
        }
      };
    }
    
    // Unknown or unsupported AssignLaborCostBy option, or required fields missing
    return {
      formula: 'Cannot Calculate Hourly Rate',
      description: `Cannot calculate hourly rate. Check that Assign Labor Cost By is properly configured and all required fields are set.`,
      values: {
        'Assign Labor Cost By': assignLaborCostByName || 'Not Set',
        'Contracted Hours': contractedHours > 0 ? `${contractedHours.toFixed(2)}` : 'Not Set',
      }
    };
  };

  // Get shift breakdown for an employee
  const getEmployeeShiftBreakdown = (employeeId: number): Array<{
    shift: Shift;
    date: Date;
    shiftHours: number;
    breakMins: number;
    breakPaid: boolean;
    hourlyRate: number;
    cost: number;
  }> => {
    const payRecord = employeePayRecords[employeeId];
    if (!payRecord) return [];

    const hourlyRate = calculateHourlyRateFromPayRecord(payRecord);
    const breakdown: Array<{
      shift: Shift;
      date: Date;
      shiftHours: number;
      breakMins: number;
      breakPaid: boolean;
      hourlyRate: number;
      cost: number;
    }> = [];

    weekDays.forEach(day => {
      const employeeShifts = getShiftsForEmployeeAndDate(employeeId, day);
      employeeShifts.forEach(shift => {
        // Use paid hours for cost calculation (deducts unpaid break if breakTimePaid is false)
        const shiftHours = getShiftPaidHoursForCost(shift);
        const breakMins = shift.unpaidHours ? Math.round(Number(shift.unpaidHours) * 60) : 0;
        const breakPaid = shift.breakTimePaid ?? false;
        const cost = hourlyRate * shiftHours;
        breakdown.push({
          shift,
          date: day,
          shiftHours,
          breakMins,
          breakPaid,
          hourlyRate,
          cost
        });
      });
    });

    return breakdown;
  };

  // Drag handlers - setData is required in dragstart for drop to fire in most browsers
  const handleDragStart = (e: React.DragEvent, template: ShiftTemplate) => {
    setDraggedTemplate(template);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `template:${template.id}`);
    e.dataTransfer.setData('application/json', JSON.stringify({ type: 'shift-template', id: template.id }));
  };

  const handleDragOver = (e: React.DragEvent, employeeId: number, date: Date) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCell(`${employeeId}-${date.toISOString()}`);
  };

  const handleDragLeave = () => {
    setDragOverCell(null);
  };

  const handleDrop = async (e: React.DragEvent, employeeId: number, date: Date) => {
    e.preventDefault();
    setDragOverCell(null);

    if (!selectedLocationId || !selectedScheduleId) return;
    
    // Prefer React state, but fall back to dataTransfer in case state wasn't set
    let template = draggedTemplate;
    if (!template) {
      try {
        const json = e.dataTransfer.getData('application/json');
        if (json) {
          const parsed = JSON.parse(json);
          if (parsed?.type === 'shift-template' && parsed.id) {
            template = shiftTemplates.find(t => t.id === parsed.id) || null;
          }
        }
      } catch {
        // Ignore parse errors and fall back to no template
      }
      if (!template) {
        const text = e.dataTransfer.getData('text/plain');
        if (text?.startsWith('template:')) {
          const id = Number(text.split(':')[1]);
          if (!Number.isNaN(id)) {
            template = shiftTemplates.find(t => t.id === id) || null;
          }
        }
      }
    }
    if (!template) return;
    const t: ShiftTemplate = template;
    
    // Prevent duplicate shift creation
    if (creatingShift) return;
    setCreatingShift(true);

    try {
      if (!checkPublishedAndWarn(async () => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const startTime = `${dateStr}T${t.startTime}:00`;
        const endTime = `${dateStr}T${t.endTime}:00`;

        // Check for overnight shift
        const startHour = parseInt(t.startTime.split(':')[0]);
        const endHour = parseInt(t.endTime.split(':')[0]);
        let finalEndTime = endTime;
        if (endHour < startHour) {
          const nextDay = addDays(date, 1);
          finalEndTime = `${format(nextDay, 'yyyy-MM-dd')}T${t.endTime}:00`;
        }

        // Check for time overlaps
        const hasOverlap = await checkTimeOverlap(employeeId, date, startTime, finalEndTime);
        if (hasOverlap) {
          alert('Cannot create shift: Time overlaps with an existing shift in this cell.');
          setDraggedTemplate(null);
          setCreatingShift(false);
          return;
        }

        // Get employee's main position
        const employee = employees.find(emp => emp.id === employeeId);
        const defaultPositionId = employee?.profile?.mainPositionId;
        const shiftColor = colorMode === 'position' && defaultPositionId 
          ? getShiftColorForMode(defaultPositionId)
          : t.color;

        await shiftApi.create({
          employeeId,
          locationId: selectedLocationId,
          scheduleId: selectedScheduleId,
          startTime,
          endTime: finalEndTime,
          color: shiftColor,
          displayOption: t.displayOption,
          unpaidHours: (t.unpaidBreakMins || 0) / 60,
          breakTimePaid: t.breakTimePaid ?? false,
          positionId: defaultPositionId,
        });
        
        // Mark schedule as changed if it was published
        if (publicationStatus?.status === 'Published' && selectedLocationId && selectedScheduleId) {
          const weekStartStr = format(weekStart, 'yyyy-MM-dd');
          await schedulePublicationStatusApi.markChanged(selectedLocationId, selectedScheduleId, weekStartStr);
          const updatedStatus = await schedulePublicationStatusApi.get(selectedLocationId, selectedScheduleId, weekStartStr);
          setPublicationStatus(updatedStatus);
        }
        
        await refreshShifts();
        await fetchShiftCounts(); // Update counts after shift creation
        setDraggedTemplate(null);
        setCreatingShift(false);
      })) {
        setDraggedTemplate(null);
        setCreatingShift(false);
        return;
      }
    } catch (error) {
      console.error('Error creating shift:', error);
      alert('Failed to create shift. Please try again.');
      setCreatingShift(false);
    }
  };

  // Refresh shifts after mutations (includes cross-schedule and cross-location shifts)
  const refreshShifts = async () => {
    if (selectedLocationId && selectedScheduleId && employees.length > 0) {
      setLoadingShifts(true);
      try {
        // Get employee IDs from employees assigned to current schedule and location
        const assignedEmployeeIds = new Set(
          employees
            .filter(emp => {
              const hasMainLocation = emp.profile?.mainLocationId === selectedLocationId;
              const hasSubLocation = emp.profile?.subLocationIds && 
                                    Array.isArray(emp.profile.subLocationIds) && 
                                    emp.profile.subLocationIds.includes(selectedLocationId);
              const hasLocation = hasMainLocation || hasSubLocation;
              const hasSchedule = emp.profile?.scheduleIds && 
                               Array.isArray(emp.profile.scheduleIds) && 
                               emp.profile.scheduleIds.includes(selectedScheduleId);
              return hasLocation && hasSchedule;
            })
            .map(emp => emp.id)
        );
        
        // Fetch current schedule shifts
        const currentScheduleShifts = await shiftApi.getAll({
          startDate: format(weekStart, 'yyyy-MM-dd'),
          endDate: format(weekEnd, 'yyyy-MM-dd'),
          locationId: selectedLocationId,
          scheduleId: selectedScheduleId,
        });
        
        // Fetch cross-schedule shifts (same location, different schedule) for assigned employees
        const crossScheduleShiftsPromises = schedules
          .filter(s => s.id !== selectedScheduleId)
          .map(schedule => 
            shiftApi.getAll({
              startDate: format(weekStart, 'yyyy-MM-dd'),
              endDate: format(weekEnd, 'yyyy-MM-dd'),
              locationId: selectedLocationId,
              scheduleId: schedule.id,
            }).then(shifts => shifts.filter(s => assignedEmployeeIds.has(s.employeeId)))
          );
        
        // Fetch cross-location shifts (different location) for assigned employees
        const crossLocationShiftsPromises = locations
          .filter(l => l.id !== selectedLocationId)
          .map(location => 
            shiftApi.getAll({
              startDate: format(weekStart, 'yyyy-MM-dd'),
              endDate: format(weekEnd, 'yyyy-MM-dd'),
              locationId: location.id,
            }).then(shifts => shifts.filter(s => assignedEmployeeIds.has(s.employeeId)))
          );
        
        const [crossScheduleShifts, crossLocationShifts] = await Promise.all([
          Promise.all(crossScheduleShiftsPromises).then(results => results.flat()),
          Promise.all(crossLocationShiftsPromises).then(results => results.flat()),
        ]);
        
        // Combine all shifts
        const allShifts = [
          ...currentScheduleShifts,
          ...crossScheduleShifts,
          ...crossLocationShifts,
        ];
        
        setShifts(allShifts);
      } catch (error) {
        console.error('Error refreshing shifts:', error);
      } finally {
        setLoadingShifts(false);
      }
    }
  };

  // Calculate hours from start and end time
  const calculateHours = (startTime: string, endTime: string, breakMins: number = 0): number => {
    if (!startTime || !endTime) return 0;
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const startTotal = startHour * 60 + startMin;
    const endTotal = endHour * 60 + endMin;
    const diffMins = endTotal > startTotal ? endTotal - startTotal : (24 * 60 - startTotal) + endTotal;
    return (diffMins - breakMins) / 60;
  };

  // Calculate default break minutes based on shift hours and break time rules
  const calculateDefaultBreakMinutes = (startTime: string, endTime: string): number => {
    if (!startTime || !endTime) return 0;
    
    // Calculate total hours without break
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const startTotal = startHour * 60 + startMin;
    const endTotal = endHour * 60 + endMin;
    const diffMins = endTotal > startTotal ? endTotal - startTotal : (24 * 60 - startTotal) + endTotal;
    const totalHours = diffMins / 60;
    
    // Find matching break time rule
    if (breakTimeRules && breakTimeRules.length > 0) {
      const matchingRule = breakTimeRules.find(rule => 
        totalHours >= rule.minHours && totalHours < rule.maxHours
      );
      
      if (matchingRule) {
        return matchingRule.breakMinutes;
      }
    }
    
    // Default to 0 if no matching rule found
    return 0;
  };

  const handleCreateShift = (employeeId: number, date: Date) => {
    if (!checkPublishedAndWarn(() => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const employee = employees.find(emp => emp.id === employeeId);
      const defaultPositionId = employee?.profile?.mainPositionId;
      const defaultColor = getShiftColorForMode(defaultPositionId);
      const defaultBreakTimePaid = breakTimeSettings?.isPaid ?? false;
      
      setBreakMinsManuallySet(false);
      setFormData({
        name: '',
        startTime: '',
        endTime: '',
        hours: 0,
        unpaidBreakMins: 0,
        breakTimePaid: defaultBreakTimePaid,
        totalHours: 0,
        color: defaultColor,
        displayOption: 'time',
        positionId: defaultPositionId,
        notes: '',
        isManualColorOverride: false,
        originalColor: defaultColor,
      });
      setEditingShift({
        id: 0,
        employeeId,
        locationId: selectedLocationId!,
        scheduleId: selectedScheduleId!,
        startTime: `${dateStr}T00:00:00`,
        endTime: `${dateStr}T00:00:00`,
        color: defaultColor,
        displayOption: 'time',
      } as Shift);
      setShowShiftForm(true);
    })) {
      return;
    }
    
    const dateStr = format(date, 'yyyy-MM-dd');
    const employee = employees.find(emp => emp.id === employeeId);
    const defaultPositionId = employee?.profile?.mainPositionId;
    const defaultColor = '#3b82f6';
    const defaultBreakTimePaid = breakTimeSettings?.isPaid ?? false;
    
    setBreakMinsManuallySet(false);
    setFormData({
      name: '',
      startTime: '',
      endTime: '',
      hours: 0,
      unpaidBreakMins: 0,
      breakTimePaid: defaultBreakTimePaid,
      totalHours: 0,
      color: defaultColor,
      displayOption: 'time',
      positionId: defaultPositionId,
      notes: '',
      isManualColorOverride: false,
      originalColor: defaultColor,
    });
    setEditingShift({
      id: 0,
      employeeId,
      locationId: selectedLocationId!,
      scheduleId: selectedScheduleId!,
      startTime: `${dateStr}T00:00:00`,
      endTime: `${dateStr}T00:00:00`,
      color: defaultColor,
      displayOption: 'time',
    } as Shift);
    setShowShiftForm(true);
  };

  const handleEditShift = (shift: Shift) => {
    // Only allow editing shifts from the current schedule and location
    if (shift.scheduleId !== selectedScheduleId || shift.locationId !== selectedLocationId) {
      return; // Don't allow editing cross-schedule or cross-location shifts
    }
    
    if (!checkPublishedAndWarn(() => {
      setEditingShift(shift);
      setBreakMinsManuallySet(true); // When editing, break minutes are already set
      const startTime = format(parseLocalDateTime(shift.startTime), 'HH:mm');
      const endTime = format(parseLocalDateTime(shift.endTime), 'HH:mm');
      const unpaidBreakMins = shift.unpaidHours ? Math.round(Number(shift.unpaidHours) * 60) : 0;
      const hours = calculateHours(startTime, endTime, unpaidBreakMins);
      const totalHours = shift.paidHours ? Number(shift.paidHours) : hours;
      
      setFormData({
        name: '',
        startTime,
        endTime,
        hours,
        unpaidBreakMins,
        breakTimePaid: shift.breakTimePaid ?? (breakTimeSettings?.isPaid ?? false),
        totalHours,
        color: shift.color || '#3b82f6',
        displayOption: (shift.displayOption as 'time' | 'f' | 'close') || 'time',
        positionId: shift.positionId || (() => {
          const emp = employees.find(e => e.id === shift.employeeId);
          return emp?.profile?.mainPositionId;
        })(),
        notes: shift.notes || '',
        isManualColorOverride: shift.isManualColorOverride || false,
        originalColor: shift.color || '#3b82f6',
      });
      setShowShiftForm(true);
    })) {
      return;
    }
    
    setEditingShift(shift);
    setBreakMinsManuallySet(true); // When editing, break minutes are already set
    const startTime = format(parseLocalDateTime(shift.startTime), 'HH:mm');
    const endTime = format(parseLocalDateTime(shift.endTime), 'HH:mm');
    const unpaidBreakMins = shift.unpaidHours ? Math.round(Number(shift.unpaidHours) * 60) : 0;
    const hours = calculateHours(startTime, endTime, unpaidBreakMins);
    const totalHours = shift.paidHours ? Number(shift.paidHours) : hours;
    
    setFormData({
      name: '',
      startTime,
      endTime,
      hours,
      unpaidBreakMins,
      breakTimePaid: shift.breakTimePaid ?? (breakTimeSettings?.isPaid ?? false),
      totalHours,
      color: shift.color || '#3b82f6',
      displayOption: (shift.displayOption as 'time' | 'f' | 'close') || 'time',
      positionId: shift.positionId || (() => {
        const emp = employees.find(e => e.id === shift.employeeId);
        return emp?.profile?.mainPositionId;
      })(),
      notes: shift.notes || '',
      isManualColorOverride: shift.isManualColorOverride || false,
      originalColor: shift.color || '#3b82f6',
    });
    setShowShiftForm(true);
  };

  const handleSaveShift = async () => {
    if (!editingShift) return;
    
    // Prevent multiple simultaneous saves
    if (savingShift) return;
    
    setSavingShift(true);

    try {
      const dateStr = format(parseLocalDateTime(editingShift.startTime), 'yyyy-MM-dd');
      const startTime = `${dateStr}T${formData.startTime}:00`;
      const endTime = `${dateStr}T${formData.endTime}:00`;
      const unpaidHours = formData.unpaidBreakMins / 60;
      const recalculatedHours = calculateHours(formData.startTime, formData.endTime, formData.unpaidBreakMins);

      // Get the date for overlap checking
      const shiftDate = parseLocalDateTime(editingShift.startTime);
      
      // Check for time overlaps (exclude current shift if editing)
      const excludeShiftId = editingShift.id !== 0 ? editingShift.id : undefined;
      const hasOverlap = await checkTimeOverlap(editingShift.employeeId, shiftDate, startTime, endTime, excludeShiftId);
      if (hasOverlap) {
        alert('Cannot save shift: Time overlaps with an existing shift in this cell.');
        setSavingShift(false);
        return;
      }

      // Use color mode unless manual override is set
      const shiftColor = formData.isManualColorOverride 
        ? formData.color 
        : getShiftColorForMode(formData.positionId);
      
      if (editingShift.id === 0) {
        await shiftApi.create({
          employeeId: editingShift.employeeId,
          locationId: editingShift.locationId,
          scheduleId: editingShift.scheduleId,
          startTime,
          endTime,
          unpaidHours,
          breakTimePaid: formData.breakTimePaid,
          color: shiftColor,
          displayOption: formData.displayOption,
          positionId: formData.positionId,
          notes: formData.notes,
          isManualColorOverride: formData.isManualColorOverride,
        });
      } else {
        await shiftApi.update(editingShift.id, {
          startTime,
          endTime,
          unpaidHours,
          breakTimePaid: formData.breakTimePaid,
          color: shiftColor,
          displayOption: formData.displayOption,
          positionId: formData.positionId,
          notes: formData.notes,
          isManualColorOverride: formData.isManualColorOverride,
        });
      }
      
      // Mark schedule as changed if it was published
      if (publicationStatus?.status === 'Published' && selectedLocationId && selectedScheduleId) {
        const weekStartStr = format(weekStart, 'yyyy-MM-dd');
        await schedulePublicationStatusApi.markChanged(selectedLocationId, selectedScheduleId, weekStartStr);
        const updatedStatus = await schedulePublicationStatusApi.get(selectedLocationId, selectedScheduleId, weekStartStr);
        setPublicationStatus(updatedStatus);
      }
      
      await refreshShifts();
      await fetchShiftCounts(); // Update counts after shift save/update
      setShowShiftForm(false);
      setEditingShift(null);
      setBreakMinsManuallySet(false);
      setHasUnsavedChanges(false);
      setShowPositionDropdown(false);
    } catch (error) {
      console.error('Error saving shift:', error);
      alert('Failed to save shift. Please try again.');
    } finally {
      setSavingShift(false);
    }
  };

  // Get available positions for an employee (main + sub positions)
  const getEmployeePositions = (employeeId: number): Position[] => {
    const employee = employees.find(emp => emp.id === employeeId);
    if (!employee?.profile) return [];
    
    const mainPositionId = employee.profile.mainPositionId;
    const subPositionIds = employee.profile.subPositionIds || [];
    const allPositionIds = [mainPositionId, ...subPositionIds].filter((id): id is number => id !== undefined && id !== null);
    
    return positions.filter(pos => allPositionIds.includes(pos.id));
  };

  // Get the color for a shift based on the color mode setting
  const getShiftDisplayColor = (shift: Shift): string => {
    if (colorMode === 'position' && shift.positionId) {
      const position = positions.find(p => p.id === shift.positionId);
      return position?.color || shift.color || '#3b82f6';
    }
    return shift.color || '#3b82f6';
  };

  // Get the color to use when creating/editing a shift based on color mode
  const getShiftColorForMode = (positionId?: number): string => {
    if (colorMode === 'position' && positionId) {
      const position = positions.find(p => p.id === positionId);
      return position?.color || '#3b82f6';
    }
    return '#3b82f6'; // Default shift color
  };

  const handleDeleteShift = async (id: number) => {
    try {
      await shiftApi.delete(id);
      
      // Mark schedule as changed if it was published
      if (publicationStatus?.status === 'Published' && selectedLocationId && selectedScheduleId) {
        const weekStartStr = format(weekStart, 'yyyy-MM-dd');
        await schedulePublicationStatusApi.markChanged(selectedLocationId, selectedScheduleId, weekStartStr);
        const updatedStatus = await schedulePublicationStatusApi.get(selectedLocationId, selectedScheduleId, weekStartStr);
        setPublicationStatus(updatedStatus);
      }
      
      await refreshShifts();
      await fetchShiftCounts(); // Update counts after shift deletion
      setShowShiftForm(false);
      setEditingShift(null);
      setBreakMinsManuallySet(false);
      setShowPositionDropdown(false);
    } catch (error) {
      console.error('Error deleting shift:', error);
    }
  };

  // Check if schedule is published and show warning; otherwise run the action
  const checkPublishedAndWarn = (action: () => void): boolean => {
    if (publicationStatus?.status === 'Published') {
      setPendingEditAction(() => action);
      setShowUnlockWarning(true);
      return false;
    }
    action();
    return true;
  };

  // Handle publish/unpublish/republish
  const handlePublish = async () => {
    if (!selectedLocationId || !selectedScheduleId) return;
    try {
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      await schedulePublicationStatusApi.publish(selectedLocationId, selectedScheduleId, weekStartStr);
      const updatedStatus = await schedulePublicationStatusApi.get(selectedLocationId, selectedScheduleId, weekStartStr);
      setPublicationStatus(updatedStatus);
      setShowActionsMenu(false);
    } catch (error) {
      console.error('Error publishing schedule:', error);
      alert('Failed to publish schedule. Please try again.');
    }
  };

  const handleUnpublish = async () => {
    if (!selectedLocationId || !selectedScheduleId) return;
    try {
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      await schedulePublicationStatusApi.unpublish(selectedLocationId, selectedScheduleId, weekStartStr);
      const updatedStatus = await schedulePublicationStatusApi.get(selectedLocationId, selectedScheduleId, weekStartStr);
      setPublicationStatus(updatedStatus);
      setShowActionsMenu(false);
    } catch (error) {
      console.error('Error unpublishing schedule:', error);
      alert('Failed to unpublish schedule. Please try again.');
    }
  };

  const handleRepublish = async () => {
    if (!selectedLocationId || !selectedScheduleId) return;
    try {
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      await schedulePublicationStatusApi.publish(selectedLocationId, selectedScheduleId, weekStartStr);
      const updatedStatus = await schedulePublicationStatusApi.get(selectedLocationId, selectedScheduleId, weekStartStr);
      setPublicationStatus(updatedStatus);
      setShowActionsMenu(false);
    } catch (error) {
      console.error('Error republishing schedule:', error);
      alert('Failed to republish schedule. Please try again.');
    }
  };

  const handleUnlockConfirm = () => {
    setShowUnlockWarning(false);
    if (pendingEditAction) {
      pendingEditAction();
      setPendingEditAction(null);
    }
  };

  const handleUnlockCancel = () => {
    setShowUnlockWarning(false);
    setPendingEditAction(null);
  };

  // Import Week handlers
  const handleWeekSelectChange = async (selectedWeekStart: Date) => {
    setImportWeekStart(selectedWeekStart);
    if (!selectedLocationId || !selectedScheduleId) return;

    try {
      const weekStartStr = format(selectedWeekStart, 'yyyy-MM-dd');
      const weekEndDate = endOfWeek(selectedWeekStart, { weekStartsOn });
      const weekEndStr = format(weekEndDate, 'yyyy-MM-dd');

      const shiftsData = await shiftApi.getAll({
        startDate: weekStartStr,
        endDate: weekEndStr,
        locationId: selectedLocationId,
        scheduleId: selectedScheduleId,
      });
      setImportShifts(shiftsData);
    } catch (error) {
      console.error('Error fetching shifts for import:', error);
      alert('Failed to fetch shifts for the selected week.');
    }
  };

  const handleImportConfirm = async () => {
    if (!selectedLocationId || !selectedScheduleId || !importWeekStart) return;

    try {
      // Delete all current week shifts
      const currentWeekShifts = shifts.filter(shift => 
        shift.locationId === selectedLocationId && 
        shift.scheduleId === selectedScheduleId
      );

      // Delete all current shifts
      for (const shift of currentWeekShifts) {
        await shiftApi.delete(shift.id);
      }

      // Create new shifts from imported week
      for (const shift of importShifts) {
        const shiftDate = parseLocalDateTime(shift.startTime);
        
        // Calculate the day offset from the imported week start
        const dayOffset = Math.floor((shiftDate.getTime() - importWeekStart.getTime()) / (1000 * 60 * 60 * 24));
        const newDate = addDays(weekStart, dayOffset);
        const dateStr = format(newDate, 'yyyy-MM-dd');
        
        const startTime = format(parseLocalDateTime(shift.startTime), 'HH:mm');
        const endTime = format(parseLocalDateTime(shift.endTime), 'HH:mm');
        
        // Handle overnight shifts
        let newStartTime = `${dateStr}T${startTime}:00`;
        let newEndTime = `${dateStr}T${endTime}:00`;
        
        const startHour = parseInt(startTime.split(':')[0]);
        const endHour = parseInt(endTime.split(':')[0]);
        if (endHour < startHour) {
          const nextDay = addDays(newDate, 1);
          newEndTime = `${format(nextDay, 'yyyy-MM-dd')}T${endTime}:00`;
        }

        await shiftApi.create({
          employeeId: shift.employeeId,
          locationId: selectedLocationId,
          scheduleId: selectedScheduleId,
          startTime: newStartTime,
          endTime: newEndTime,
          unpaidHours: shift.unpaidHours,
          breakTimePaid: shift.breakTimePaid ?? false,
          color: shift.color,
          displayOption: shift.displayOption,
          positionId: shift.positionId,
          notes: shift.notes,
          isManualColorOverride: shift.isManualColorOverride,
        });
      }

      // Mark schedule as changed if it was published
      if (publicationStatus?.status === 'Published' && selectedLocationId && selectedScheduleId) {
        const weekStartStr = format(weekStart, 'yyyy-MM-dd');
        await schedulePublicationStatusApi.markChanged(selectedLocationId, selectedScheduleId, weekStartStr);
        const updatedStatus = await schedulePublicationStatusApi.get(selectedLocationId, selectedScheduleId, weekStartStr);
        setPublicationStatus(updatedStatus);
      }

      // Refresh shifts
      await refreshShifts();
      await fetchShiftCounts(); // Update counts after importing shifts
      
      setShowImportConfirm(false);
      setShowImportModal(false);
      setImportWeekStart(null);
      setImportShifts([]);
      
      alert(`Successfully imported ${importShifts.length} records.`);
    } catch (error) {
      console.error('Error importing week:', error);
      alert('Failed to import week. Please try again.');
    }
  };

  // Calculate import summary
  const importSummary = useMemo(() => {
    if (importShifts.length === 0) return { totalHours: 0, recordCount: 0 };
    
    const totalHours = importShifts.reduce((sum, shift) => {
      const paidHours = shift.paidHours || 0;
      const unpaidHours = shift.unpaidHours || 0;
      return sum + Number(paidHours) + Number(unpaidHours);
    }, 0);
    
    return {
      totalHours,
      recordCount: importShifts.length,
    };
  }, [importShifts]);

  // Check if employee has any shifts for the current week
  const hasShiftsForWeek = (employeeId: number): boolean => {
    return shifts.some(shift => {
      if (shift.employeeId !== employeeId) return false;
      const shiftDate = parseLocalDateTime(shift.startTime);
      return shiftDate >= weekStart && shiftDate <= weekEnd;
    });
  };

  // Push empty rows to bottom while maintaining current sort order
  const handlePushEmptyRowsToBottom = async () => {
    if (!selectedLocationId || !selectedScheduleId) return;

    try {
      // Get current employee order (with or without sort order)
      const currentOrder = employeeSortOrder.length > 0 
        ? [...employeeSortOrder]
        : filteredEmployees.map((emp, index) => ({
            employeeId: emp.id,
            sortOrder: index + 1
          }));

      // Separate employees into those with shifts and those without
      const employeesWithShifts: typeof currentOrder = [];
      const employeesWithoutShifts: typeof currentOrder = [];

      currentOrder.forEach(orderItem => {
        if (hasShiftsForWeek(orderItem.employeeId)) {
          employeesWithShifts.push(orderItem);
        } else {
          employeesWithoutShifts.push(orderItem);
        }
      });

      // Reorder: employees with shifts first (maintaining their order), then employees without shifts (maintaining their order)
      const newOrder = [
        ...employeesWithShifts.map((item, idx) => ({ ...item, sortOrder: idx + 1 })),
        ...employeesWithoutShifts.map((item, idx) => ({ ...item, sortOrder: employeesWithShifts.length + idx + 1 }))
      ];

      setEmployeeSortOrder(newOrder);

      // Save to database
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      await employeeSortOrderApi.save(selectedLocationId, selectedScheduleId, weekStartStr, newOrder);
    } catch (error) {
      console.error('Error pushing empty rows to bottom:', error);
      alert('Failed to reorder employees. Please try again.');
    }
  };

  const navigateWeek = (direction: 'prev' | 'next' | 'today') => {
    setLoadingShifts(true);
    setShifts([]);
    if (direction === 'prev') {
      setCurrentWeek(addDays(currentWeek, -7));
    } else if (direction === 'next') {
      setCurrentWeek(addDays(currentWeek, 7));
    } else {
      setCurrentWeek(new Date());
    }
  };

  return (
    <div className="p-4 h-full flex flex-col min-h-0">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between flex-shrink-0 relative">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigateWeek('prev')}
            className="p-2 border rounded hover:bg-gray-50"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigateWeek('today')}
            className="px-4 py-2 border rounded hover:bg-gray-50"
          >
            This Week
          </button>
          <button
            onClick={() => navigateWeek('next')}
            className="p-2 border rounded hover:bg-gray-50"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <span className="ml-4 font-semibold">
            {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
          </span>
        </div>

        {/* Publication Status Badge - Centered */}
        <div className="absolute left-1/2 transform -translate-x-1/2">
          {publicationStatus?.status === 'Published' && (
            <div className="flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1.5 rounded-full text-sm font-semibold">
              <CheckCircle className="w-4 h-4" />
              <span>Schedule Published</span>
            </div>
          )}
          {publicationStatus?.status === 'Changed' && (
            <div className="flex items-center gap-2 bg-orange-100 text-orange-800 px-3 py-1.5 rounded-full text-sm font-semibold">
              <AlertCircle className="w-4 h-4" />
              <span>Schedule Changed</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <select
            value={selectedScheduleId || ''}
            onChange={(e) => {
              setLoadingShifts(true);
              setSelectedScheduleId(Number(e.target.value));
            }}
            className="px-3 py-2 border rounded"
          >
            <option value="">Select Schedule</option>
            {schedules.map(sched => {
              const counts = scheduleCounts[sched.id];
              const employeesWithShifts = counts?.uniqueEmployees || 0;
              const shiftCount = counts?.totalShifts || 0;
              const totalEmployeesAssigned = counts?.totalEmployeesAssigned || 0;
              const displayText = counts && (totalEmployeesAssigned > 0 || employeesWithShifts > 0 || shiftCount > 0)
                ? `${sched.name} 👥${totalEmployeesAssigned} 👤${employeesWithShifts} 🕐${shiftCount}`
                : sched.name;
              return (
                <option key={sched.id} value={sched.id} title={`${totalEmployeesAssigned} total employees assigned, ${employeesWithShifts} with shifts this week, ${shiftCount} total shifts`}>
                  {displayText}
                </option>
              );
            })}
          </select>
          
          {/* Actions Button */}
          {selectedScheduleId && (
            <div className="relative">
              <button
                onClick={() => setShowActionsMenu(!showActionsMenu)}
                className="px-3 py-2 border rounded hover:bg-gray-50 flex items-center gap-1"
              >
                <span>Actions</span>
                <MoreVertical className="w-4 h-4" />
              </button>
              
              {showActionsMenu && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowActionsMenu(false)}
                  />
                  <div className="absolute right-0 mt-1 w-48 bg-white border rounded shadow-lg z-20">
                    <button
                      onClick={() => {
                        setShowImportModal(true);
                        setShowActionsMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2 border-b"
                    >
                      <Download className="w-4 h-4" />
                      <span>Import Week</span>
                    </button>
                    {publicationStatus?.status === 'Published' ? (
                      <button
                        onClick={handleUnpublish}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Unlock className="w-4 h-4" />
                        <span>Unpublish Schedule</span>
                      </button>
                    ) : publicationStatus?.status === 'Changed' ? (
                      <button
                        onClick={handleRepublish}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Lock className="w-4 h-4" />
                        <span>Re-publish Schedule</span>
                      </button>
                    ) : (
                      <button
                        onClick={handlePublish}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Lock className="w-4 h-4" />
                        <span>Publish Schedule</span>
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Show blank screen if "All Locations" is selected */}
      {!selectedLocationId ? (
        <div className="flex-1 flex flex-col items-center justify-center bg-gray-50">
          <MapPin className="w-24 h-24 text-gray-400 mb-4" />
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">Select a Location</h2>
          <p className="text-gray-600 text-center max-w-md">
            Please select a specific location from the dropdown in the top navigation bar to access scheduling features.
          </p>
        </div>
      ) : (
        <div className="flex-1 flex gap-4 min-h-0" style={{ height: 'calc(100vh - 250px)' }}>
          {/* Shift Timings Sidebar */}
        <div className="w-64 border rounded-lg p-4 overflow-y-auto" style={{ maxHeight: '100%' }}>
          <h3 className="font-semibold mb-3 sticky top-0 bg-white pb-2 z-10">Shift Timings</h3>
          
          {/* Color Mode Radio Buttons */}
          <div className="mb-4 pb-3 border-b sticky top-[40px] bg-white z-10">
            <div className="text-xs font-medium text-gray-700 mb-2">Color By</div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="colorMode"
                  value="shift"
                  checked={colorMode === 'shift'}
                  onChange={(e) => setColorMode(e.target.value as 'shift' | 'position')}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-sm text-gray-700">Shift Color</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="colorMode"
                  value="position"
                  checked={colorMode === 'position'}
                  onChange={(e) => setColorMode(e.target.value as 'shift' | 'position')}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-sm text-gray-700">Position Color</span>
              </label>
            </div>
          </div>
          
          <div className="space-y-2">
            {shiftTemplates.map(template => (
              <div
                key={template.id}
                draggable
                onDragStart={(e) => handleDragStart(e, template)}
                onDragEnd={() => setDraggedTemplate(null)}
                className="p-3 border rounded cursor-move hover:bg-gray-50 flex items-center justify-between"
                style={{ backgroundColor: template.color + '20', borderColor: template.color }}
              >
                <div className="font-medium text-sm">
                  {template.startTime} - {template.endTime}
                </div>
                <div className="font-medium text-sm">
                  {template.hours}h
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="flex-1 border rounded-lg flex flex-col relative" style={{ maxHeight: '100%' }}>
          {/* Loading Overlay */}
          {loadingShifts && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50 rounded-lg">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                <div className="text-sm text-gray-600 font-medium">Loading shifts...</div>
              </div>
            </div>
          )}
          
          {/* Header Row */}
          <div className="grid grid-cols-9 border-b sticky top-0 bg-white z-10 flex-shrink-0">
            <div className="p-3 font-semibold border-r border-gray-300">Employee</div>
            {weekDays.map(day => (
              <div key={day.toISOString()} className="p-3 text-center border-r border-gray-300">
                <div className="font-semibold">{format(day, 'EEE')}</div>
                <div className="text-xs text-gray-500">{format(day, 'MMM d')}</div>
              </div>
            ))}
            <div className="p-3 font-semibold text-center border-l border-gray-100 bg-gray-50 relative" ref={totalHeaderDropdownRef}>
              <div className="flex items-center justify-center gap-1">
                <div className="flex flex-col items-center">
                  <div>Total</div>
                  <div className="text-xs font-normal">Week</div>
                </div>
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowTotalDropdown(!showTotalDropdown);
                    }}
                    className="p-0.5 hover:bg-gray-200 rounded flex items-center justify-center transition-colors flex-shrink-0"
                    title="Toggle display mode"
                  >
                    <ChevronRight className={`w-3 h-3 text-gray-700 transition-transform ${showTotalDropdown ? 'rotate-90' : ''}`} />
                  </button>
                  {showTotalDropdown && (
                    <div className="absolute left-full -top-5 ml-0.5 bg-white border border-gray-300 rounded shadow-lg z-50 min-w-[60px]">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setTotalDisplayMode('hours');
                          setShowTotalDropdown(false);
                        }}
                        className={`w-full text-left px-2 py-1.5 text-xs hover:bg-gray-100 whitespace-nowrap ${totalDisplayMode === 'hours' ? 'bg-blue-50 font-semibold' : ''}`}
                      >
                        Hours
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setTotalDisplayMode('cost');
                          setShowTotalDropdown(false);
                        }}
                        className={`w-full text-left px-2 py-1.5 text-xs hover:bg-gray-100 whitespace-nowrap ${totalDisplayMode === 'cost' ? 'bg-blue-50 font-semibold' : ''}`}
                      >
                        Cost
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Search and Sort Bar */}
          <div className="border-b bg-gray-50 p-2 flex items-center justify-between gap-2 sticky top-[60px] z-10 flex-shrink-0">
            <div className="flex-1 flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 pl-8 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400">🔍</span>
              </div>
            </div>
            <button
              onClick={handlePushEmptyRowsToBottom}
              className="px-4 py-2 border rounded-md hover:bg-gray-100 flex items-center gap-2 flex-shrink-0"
              title="Push empty rows to bottom"
            >
              <ArrowDownToLine className="w-4 h-4" />
              <span>Push Empty</span>
            </button>
            <button
              onClick={() => {
                // Initialize sort order: use existing order if available, otherwise use current employee order
                if (employeeSortOrder.length === 0) {
                  const currentOrder = filteredEmployees.map((emp, index) => ({
                    employeeId: emp.id,
                    sortOrder: index + 1
                  }));
                  setEmployeeSortOrder(currentOrder);
                }
                setShowSortModal(true);
              }}
              className="px-4 py-2 border rounded-md hover:bg-gray-100 flex items-center gap-2 flex-shrink-0"
            >
              <span>Sort</span>
              <span>⇅</span>
            </button>
          </div>

          {/* Employee Rows - Scrollable area */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {filteredEmployees.map(employee => {
              const isDragged = draggedEmployeeId === employee.id;
              const isDragOver = dragOverEmployeeId === employee.id;
              
              return (
                <div 
                  key={employee.id} 
                  className={`grid grid-cols-9 border-b ${isDragged ? 'opacity-50' : ''} ${isDragOver ? 'bg-blue-50' : ''}`}
                  draggable
                  onDragStart={(e) => {
                    setDraggedEmployeeId(employee.id);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragEnd={() => {
                    setDraggedEmployeeId(null);
                    setDragOverEmployeeId(null);
                  }}
                  onDragOver={(e) => {
                    // Only be a drop target when dragging employee rows (reorder), not shift templates
                    if (!draggedEmployeeId) {
                      return; // shift template drag - let cell handle it
                    }
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    if (dragOverEmployeeId !== employee.id) {
                      setDragOverEmployeeId(employee.id);
                    }
                  }}
                  onDragLeave={() => {
                    setDragOverEmployeeId(null);
                  }}
                  onDrop={async (e) => {
                    e.preventDefault();
                    e.stopPropagation(); // Prevent shift drop handler from firing
                    if (draggedEmployeeId && draggedEmployeeId !== employee.id) {
                      // Update sort order
                      const newOrder = [...employeeSortOrder];
                      const draggedIndex = newOrder.findIndex(o => o.employeeId === draggedEmployeeId);
                      const targetIndex = newOrder.findIndex(o => o.employeeId === employee.id);
                      
                      if (draggedIndex !== -1 && targetIndex !== -1) {
                        // Both employees are in sort order - reorder
                        const [removed] = newOrder.splice(draggedIndex, 1);
                        newOrder.splice(targetIndex, 0, removed);
                        // Reassign sort orders
                        newOrder.forEach((item, idx) => {
                          item.sortOrder = idx + 1;
                        });
                      } else if (draggedIndex === -1 && targetIndex !== -1) {
                        // Dragged employee not in sort order, add it at target position
                        newOrder.splice(targetIndex, 0, { employeeId: draggedEmployeeId, sortOrder: targetIndex + 1 });
                        newOrder.forEach((item, idx) => {
                          item.sortOrder = idx + 1;
                        });
                      } else if (draggedIndex !== -1 && targetIndex === -1) {
                        // Target employee not in sort order, add it after dragged position
                        const insertIndex = draggedIndex + 1;
                        newOrder.splice(insertIndex, 0, { employeeId: employee.id, sortOrder: insertIndex + 1 });
                        newOrder.forEach((item, idx) => {
                          item.sortOrder = idx + 1;
                        });
                      } else {
                        // Neither in sort order - initialize with both
                        const draggedEmp = filteredEmployees.find(e => e.id === draggedEmployeeId);
                        const targetEmp = filteredEmployees.find(e => e.id === employee.id);
                        if (draggedEmp && targetEmp) {
                          const draggedPos = filteredEmployees.findIndex(e => e.id === draggedEmployeeId);
                          const targetPos = filteredEmployees.findIndex(e => e.id === employee.id);
                          // Add both employees maintaining their relative order
                          if (draggedPos < targetPos) {
                            newOrder.push({ employeeId: draggedEmployeeId, sortOrder: newOrder.length + 1 });
                            newOrder.push({ employeeId: employee.id, sortOrder: newOrder.length + 1 });
                          } else {
                            newOrder.push({ employeeId: employee.id, sortOrder: newOrder.length + 1 });
                            newOrder.push({ employeeId: draggedEmployeeId, sortOrder: newOrder.length + 1 });
                          }
                          newOrder.forEach((item, idx) => {
                            item.sortOrder = idx + 1;
                          });
                        }
                      }
                      
                      setEmployeeSortOrder(newOrder);
                      
                      // Save to database immediately
                      if (selectedLocationId && selectedScheduleId) {
                        try {
                          const weekStartStr = format(weekStart, 'yyyy-MM-dd');
                          await employeeSortOrderApi.save(selectedLocationId, selectedScheduleId, weekStartStr, newOrder);
                        } catch (error) {
                          console.error('Error saving sort order:', error);
                          // Revert on error
                          setEmployeeSortOrder([...employeeSortOrder]);
                        }
                      }
                    }
                    setDraggedEmployeeId(null);
                    setDragOverEmployeeId(null);
                  }}
                >
                  <div className="p-3 border-r border-gray-300 flex items-center gap-2 cursor-move">
                    <GripVertical className="w-4 h-4 text-gray-400" />
                    <div>
                      <div className="font-medium text-sm">
                        {employee.firstName} {employee.lastName}
                      </div>
                      <div className="text-xs text-gray-500">{employee.email}</div>
                    </div>
                  </div>
                {weekDays.map(day => {
                  const dayShifts = getShiftsForEmployeeAndDate(employee.id, day);
                  const cellKey = `${employee.id}-${day.toISOString()}`;
                  const isDropTarget = dragOverCell === cellKey;
                  const isHovered = hoveredCell === cellKey;
                  const isEmpty = dayShifts.length === 0;
                  // Check if there are any shifts from the current schedule
                  const hasCurrentScheduleShift = dayShifts.some(shift => 
                    shift.scheduleId === selectedScheduleId && shift.locationId === selectedLocationId
                  );
                  // Show + icon if empty OR if only cross-schedule/cross-location shifts exist
                  const canAddShift = isEmpty || !hasCurrentScheduleShift;

                  return (
                    <div
                      key={day.toISOString()}
                      data-cell-key={cellKey}
                      className={`p-2 min-h-20 border-r border-gray-300 last:border-r-0 relative group ${
                        isDropTarget ? 'bg-blue-50' : 'bg-white'
                      }`}
                      onMouseEnter={() => setHoveredCell(cellKey)}
                      onMouseLeave={() => {
                        setHoveredCell(null);
                        setHoveredShiftId(null);
                      }}
                      onDragEnter={(e) => {
                        if (!draggedEmployeeId) {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                        }
                      }}
                      onDragOver={(e) => {
                        // Only handle shift template drag, not employee row drag
                        if (!draggedEmployeeId) {
                          handleDragOver(e, employee.id, day);
                        }
                      }}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => {
                        if (!draggedEmployeeId) handleDrop(e, employee.id, day);
                      }}
                    >
                      {/* Show + icon on empty cells or cells with only cross-schedule/cross-location shifts when hovering */}
                      {canAddShift && isHovered && (
                        <button
                          onClick={() => {
                            const dateStr = format(day, 'yyyy-MM-dd');
                            setEditingShift({
                              id: 0,
                              employeeId: employee.id,
                              locationId: selectedLocationId!,
                              scheduleId: selectedScheduleId!,
                              startTime: `${dateStr}T00:00:00`,
                              endTime: `${dateStr}T00:00:00`,
                              color: '#3b82f6',
                              displayOption: 'time',
                            } as Shift);
                            setBreakMinsManuallySet(false);
                            const defaultBreakTimePaid = breakTimeSettings?.isPaid ?? false;
                            setFormData({
                              name: '',
                              startTime: '',
                              endTime: '',
                              hours: 0,
                              unpaidBreakMins: 0,
                              breakTimePaid: defaultBreakTimePaid,
                              totalHours: 0,
                              color: '#3b82f6',
                              displayOption: 'time',
                              positionId: employee.profile?.mainPositionId,
                              notes: '',
                              isManualColorOverride: false,
                              originalColor: '#3b82f6',
                            });
                            setShowShiftForm(true);
                          }}
                          className="absolute top-1 right-1 p-1 hover:bg-blue-100 rounded bg-white shadow-sm z-10"
                          title="Add shift"
                        >
                          <Plus className="w-4 h-4 text-blue-600" />
                        </button>
                      )}

                      {/* Show paste icon if shift is copied and cell is hovered */}
                      {copiedShift && isHovered && (
                        <button
                          onClick={async () => {
                            if (!checkPublishedAndWarn(async () => {
                              if (copiedShift && selectedLocationId && selectedScheduleId) {
                                const dateStr = format(day, 'yyyy-MM-dd');
                                const startTime = format(parseLocalDateTime(copiedShift.startTime), 'HH:mm');
                                const endTime = format(parseLocalDateTime(copiedShift.endTime), 'HH:mm');
                                
                                // Check for time overlaps with existing shifts
                                const newStartTime = `${dateStr}T${startTime}:00`;
                                const newEndTime = `${dateStr}T${endTime}:00`;
                                
                                const hasOverlap = await checkTimeOverlap(employee.id, day, newStartTime, newEndTime);
                                if (hasOverlap) {
                                  alert('Cannot paste shift: Time overlaps with an existing shift in this cell.');
                                  return;
                                }
                                
                                try {
                                  const copyColor = colorMode === 'position' && copiedShift.positionId
                                    ? getShiftColorForMode(copiedShift.positionId)
                                    : copiedShift.color;
                                  
                                  await shiftApi.create({
                                    employeeId: employee.id,
                                    locationId: selectedLocationId,
                                    scheduleId: selectedScheduleId,
                                    startTime: newStartTime,
                                    endTime: newEndTime,
                                    color: copyColor,
                                    displayOption: copiedShift.displayOption,
                                    unpaidHours: copiedShift.unpaidHours,
                                    breakTimePaid: copiedShift.breakTimePaid ?? false,
                                    positionId: copiedShift.positionId,
                                    notes: copiedShift.notes,
                                    isManualColorOverride: copiedShift.isManualColorOverride,
                                  });
                                  
                                  // Mark schedule as changed if it was published
                                  if (publicationStatus?.status === 'Published' && selectedLocationId && selectedScheduleId) {
                                    const weekStartStr = format(weekStart, 'yyyy-MM-dd');
                                    await schedulePublicationStatusApi.markChanged(selectedLocationId, selectedScheduleId, weekStartStr);
                                    const updatedStatus = await schedulePublicationStatusApi.get(selectedLocationId, selectedScheduleId, weekStartStr);
                                    setPublicationStatus(updatedStatus);
                                  }
                                  
                                  await refreshShifts();
                                  await fetchShiftCounts(); // Update counts after pasting shift
                                } catch (error) {
                                  console.error('Error pasting shift:', error);
                                  alert('Failed to paste shift. Please try again.');
                                }
                              }
                            })) {
                              return;
                            }
                            
                            if (copiedShift && selectedLocationId && selectedScheduleId) {
                              const dateStr = format(day, 'yyyy-MM-dd');
                              const startTime = format(parseLocalDateTime(copiedShift.startTime), 'HH:mm');
                              const endTime = format(parseLocalDateTime(copiedShift.endTime), 'HH:mm');
                              
                              // Check for time overlaps with existing shifts
                              const newStartTime = `${dateStr}T${startTime}:00`;
                              const newEndTime = `${dateStr}T${endTime}:00`;
                              
                              const hasOverlap = await checkTimeOverlap(employee.id, day, newStartTime, newEndTime);
                              if (hasOverlap) {
                                alert('Cannot paste shift: Time overlaps with an existing shift in this cell.');
                                return;
                              }
                              
                              try {
                                const copyColor = colorMode === 'position' && copiedShift.positionId
                                  ? getShiftColorForMode(copiedShift.positionId)
                                  : copiedShift.color;
                                
                                await shiftApi.create({
                                  employeeId: employee.id,
                                  locationId: selectedLocationId,
                                  scheduleId: selectedScheduleId,
                                  startTime: newStartTime,
                                  endTime: newEndTime,
                                  color: copyColor,
                                  displayOption: copiedShift.displayOption,
                                  unpaidHours: copiedShift.unpaidHours,
                                  breakTimePaid: copiedShift.breakTimePaid ?? false,
                                  positionId: copiedShift.positionId,
                                  notes: copiedShift.notes,
                                  isManualColorOverride: copiedShift.isManualColorOverride,
                                });
                                
                                // Mark schedule as changed if it was published
                                if (publicationStatus?.status === 'Published' && selectedLocationId && selectedScheduleId) {
                                  const weekStartStr = format(weekStart, 'yyyy-MM-dd');
                                  await schedulePublicationStatusApi.markChanged(selectedLocationId, selectedScheduleId, weekStartStr);
                                  const updatedStatus = await schedulePublicationStatusApi.get(selectedLocationId, selectedScheduleId, weekStartStr);
                                  setPublicationStatus(updatedStatus);
                                }
                                
                                await refreshShifts();
                                await fetchShiftCounts(); // Update counts after pasting shift
                              } catch (error) {
                                console.error('Error pasting shift:', error);
                                alert('Failed to paste shift. Please try again.');
                              }
                            }
                          }}
                          className="absolute bottom-1 right-1 p-1 hover:bg-green-100 rounded bg-white shadow-sm z-10"
                          title="Paste shift"
                        >
                          <Clipboard className="w-4 h-4 text-green-600" />
                        </button>
                      )}

                      {dayShifts.map(shift => {
                        const isShiftHovered = hoveredShiftId === shift.id;
                        const isCurrentSchedule = shift.scheduleId === selectedScheduleId && shift.locationId === selectedLocationId;
                        const isCrossSchedule = shift.locationId === selectedLocationId && shift.scheduleId !== selectedScheduleId;
                        const isCrossLocation = shift.locationId !== selectedLocationId;
                        
                        // Determine styling based on shift type
                        const shiftStyle = isCurrentSchedule 
                          ? { backgroundColor: getShiftDisplayColor(shift), color: 'white' }
                          : { backgroundColor: 'transparent', color: isCrossLocation ? '#9ca3af' : '#374151' };
                        
                        const shiftClassName = isCurrentSchedule
                          ? "p-2 mb-1 rounded text-xs relative"
                          : isCrossLocation
                          ? "p-2 mb-1 rounded text-xs relative opacity-60"
                          : "p-2 mb-1 rounded text-xs relative border border-gray-300";
                        
                        return (
                          <div
                            key={shift.id}
                            className={shiftClassName}
                            style={shiftStyle}
                            onMouseEnter={() => setHoveredShiftId(shift.id)}
                            onMouseLeave={() => setHoveredShiftId(null)}
                          >
                            {isShiftHovered && isCurrentSchedule ? (
                              <div className="flex items-center justify-center gap-3 bg-white bg-opacity-20 rounded p-1.5">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCopiedShift(shift);
                                  }}
                                  className="p-1.5 hover:bg-white hover:bg-opacity-30 rounded transition-colors"
                                  title="Copy shift"
                                >
                                  <Copy className="w-4 h-4 text-white stroke-[2.5]" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const dateStr = format(day, 'yyyy-MM-dd');
                                    setEditingShift({
                                      id: 0,
                                      employeeId: employee.id,
                                      locationId: selectedLocationId!,
                                      scheduleId: selectedScheduleId!,
                                      startTime: `${dateStr}T00:00:00`,
                                      endTime: `${dateStr}T00:00:00`,
                                      color: '#3b82f6',
                                      displayOption: 'time',
                                    } as Shift);
                                    setBreakMinsManuallySet(false);
                                    const defaultBreakTimePaid = breakTimeSettings?.isPaid ?? false;
                                    setFormData({
                                      name: '',
                                      startTime: '',
                                      endTime: '',
                                      hours: 0,
                                      unpaidBreakMins: 0,
                                      breakTimePaid: defaultBreakTimePaid,
                                      totalHours: 0,
                                      color: '#3b82f6',
                                      displayOption: 'time',
                                      positionId: employee.profile?.mainPositionId,
                                      notes: '',
                                      isManualColorOverride: false,
                                      originalColor: '#3b82f6',
                                    });
                                    setShowShiftForm(true);
                                  }}
                                  className="p-1.5 hover:bg-white hover:bg-opacity-30 rounded transition-colors"
                                  title="Add shift"
                                >
                                  <Plus className="w-4 h-4 text-white stroke-[2.5]" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditShift(shift);
                                  }}
                                  className="p-1.5 hover:bg-white hover:bg-opacity-30 rounded transition-colors"
                                  title="Edit shift"
                                >
                                  <Edit className="w-4 h-4 text-white stroke-[2.5]" />
                                </button>
                              </div>
                            ) : (
                              <>
                                <div 
                                  className={`font-semibold ${isCurrentSchedule ? 'cursor-pointer' : 'cursor-default'}`}
                                  onClick={() => {
                                    if (isCurrentSchedule) {
                                      handleEditShift(shift);
                                    }
                                  }}
                                >
                                  {format(parseLocalDateTime(shift.startTime), 'HH:mm')} - {format(parseLocalDateTime(shift.endTime), 'HH:mm')}
                                </div>
                                {shift.position && (
                                  <div className={`text-xs ${isCurrentSchedule ? 'opacity-90' : 'opacity-70'}`}>
                                    {shift.position.title}
                                  </div>
                                )}
                                {isCrossSchedule && shift.schedule && (
                                  <div className="text-xs opacity-70 italic">
                                    {shift.schedule.name}
                                  </div>
                                )}
                                {isCrossLocation && (() => {
                                  const location = locations.find(l => l.id === shift.locationId);
                                  return location ? (
                                    <div className="text-xs opacity-70 italic">
                                      {location.name}
                                    </div>
                                  ) : null;
                                })()}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
                {/* Total column for employee */}
                <div className="p-3 border-l border-gray-100 bg-gray-50 text-center">
                  <div className="font-semibold">
                    {totalDisplayMode === 'cost' 
                      ? (
                        <button
                          onClick={() => {
                            setSelectedEmployeeForBreakdown(employee);
                            setShowCostBreakdownModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                        >
                          €{(totalsCache.employeeCostCurrent[employee.id] ?? 0).toFixed(2)}
                        </button>
                      )
                      : `${(totalsCache.employeeHoursCurrent[employee.id] ?? 0).toFixed(2)}h`
                    }
                  </div>
                  {/* All schedules total (same location) - smaller font */}
                  {(() => {
                    const allSchedulesTotal = totalDisplayMode === 'cost'
                      ? (totalsCache.employeeCostAll[employee.id] ?? 0)
                      : (totalsCache.employeeHoursAll[employee.id] ?? 0);
                    const currentScheduleTotal = totalDisplayMode === 'cost'
                      ? (totalsCache.employeeCostCurrent[employee.id] ?? 0)
                      : (totalsCache.employeeHoursCurrent[employee.id] ?? 0);
                    
                    // Only show if all schedules total is different from current schedule total
                    if (Math.abs(allSchedulesTotal - currentScheduleTotal) > 0.01) {
                      return (
                        <div className="text-xs text-gray-500 mt-1">
                          {totalDisplayMode === 'cost'
                            ? `All: €${allSchedulesTotal.toFixed(2)}`
                            : `All: ${allSchedulesTotal.toFixed(2)}h`
                          }
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            );
            })}
          </div>
          
          {/* Total Row - Fixed at bottom */}
          <div className="grid grid-cols-9 border-t-2 border-gray-400 bg-gray-100 flex-shrink-0">
            <div className="p-3 font-semibold border-r border-gray-300 bg-gray-100">
              Total
            </div>
            {weekDays.map((day, index) => (
              <div 
                key={day.toISOString()} 
                className={`p-2 text-center ${index < weekDays.length - 1 ? 'border-r border-gray-300' : ''}`}
              >
                <div className="font-semibold">
                  {totalDisplayMode === 'cost'
                    ? `€${(totalsCache.dayCostCurrent[format(day, 'yyyy-MM-dd')] ?? 0).toFixed(2)}`
                    : `${(totalsCache.dayHoursCurrent[format(day, 'yyyy-MM-dd')] ?? 0).toFixed(2)}h`
                  }
                </div>
                {/* All schedules day total (same location) - smaller font */}
                {(() => {
                  const dayStr = format(day, 'yyyy-MM-dd');
                  const allSchedulesDayTotal = totalDisplayMode === 'cost'
                    ? (totalsCache.dayCostAll[dayStr] ?? 0)
                    : (totalsCache.dayHoursAll[dayStr] ?? 0);
                  const currentScheduleDayTotal = totalDisplayMode === 'cost'
                    ? (totalsCache.dayCostCurrent[dayStr] ?? 0)
                    : (totalsCache.dayHoursCurrent[dayStr] ?? 0);
                  
                  // Only show if all schedules total is different from current schedule total
                  if (Math.abs(allSchedulesDayTotal - currentScheduleDayTotal) > 0.01) {
                    return (
                      <div className="text-xs text-gray-600 mt-1">
                        {totalDisplayMode === 'cost'
                          ? `All: €${allSchedulesDayTotal.toFixed(2)}`
                          : `All: ${allSchedulesDayTotal.toFixed(2)}h`
                        }
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            ))}
            <div className="p-3 text-center border-l border-gray-200 bg-gray-200">
              <div className="flex items-center justify-center gap-2">
                <div className="font-semibold">
                  {totalDisplayMode === 'cost'
                    ? `€${totalsCache.grandCostCurrent.toFixed(2)}`
                    : `${totalsCache.grandHoursCurrent.toFixed(2)}h`
                  }
                </div>
                <button
                  onClick={() => setShowScheduleBreakdownModal(true)}
                  className="text-blue-600 hover:text-blue-800 transition-colors p-0.5 rounded hover:bg-blue-50"
                  title="View schedule breakdown"
                >
                  <Calculator className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Shift Form Modal */}
      {showShiftForm && editingShift && (() => {
        const employeePositions = getEmployeePositions(editingShift.employeeId);
        // Find "No Position" from the positions list
        const noPosition = positions.find(pos => pos.title.toLowerCase() === 'no position');
        // Combine employee positions with "No Position" if it exists and is not already in employee positions
        const employeePositionIds = new Set(employeePositions.map(pos => pos.id));
        const allAvailablePositions = noPosition && !employeePositionIds.has(noPosition.id)
          ? [...employeePositions, noPosition]
          : employeePositions;
        const recalculatedHours = calculateHours(formData.startTime, formData.endTime, formData.unpaidBreakMins);
        const breakHours = formData.unpaidBreakMins / 60;
        const recalculatedTotalHours = formData.breakTimePaid ? recalculatedHours + breakHours : recalculatedHours;
        
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-xl my-8">
            <h3 className="text-lg font-semibold mb-4">
              {editingShift.id === 0 ? 'Create Shift' : 'Edit Shift'}
            </h3>
            <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">

                {/* Start Time and End Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Start Time</label>
                    <input
                      type="time"
                      value={formData.startTime}
                      onChange={(e) => {
                        const newStartTime = e.target.value;
                        const newEndTime = formData.endTime;
                        
                        // Auto-calculate break minutes if both times are filled and break minutes hasn't been manually set
                        let newBreakMins = formData.unpaidBreakMins || 0;
                        if (newStartTime && newEndTime && !breakMinsManuallySet) {
                          newBreakMins = calculateDefaultBreakMinutes(newStartTime, newEndTime);
                        }
                        
                        const newHours = calculateHours(newStartTime, newEndTime, newBreakMins);
                        // Total Hours: if break is unpaid, use hours (break already deducted)
                        // If break is paid, add break back to get total shift hours
                        const breakHours = newBreakMins / 60;
                        const newTotalHours = formData.breakTimePaid ? newHours + breakHours : newHours;
                        setFormData({ 
                          ...formData, 
                          startTime: newStartTime,
                          unpaidBreakMins: newBreakMins,
                          hours: newHours,
                          totalHours: newTotalHours
                        });
                      }}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">End Time</label>
                    <input
                      type="time"
                      value={formData.endTime}
                      onChange={(e) => {
                        const newEndTime = e.target.value;
                        const newStartTime = formData.startTime;
                        
                        // Auto-calculate break minutes if both times are filled and break minutes hasn't been manually set
                        let newBreakMins = formData.unpaidBreakMins || 0;
                        if (newStartTime && newEndTime && !breakMinsManuallySet) {
                          newBreakMins = calculateDefaultBreakMinutes(newStartTime, newEndTime);
                        }
                        
                        const newHours = calculateHours(newStartTime, newEndTime, newBreakMins);
                        // Total Hours: if break is unpaid, use hours (break already deducted)
                        // If break is paid, add break back to get total shift hours
                        const breakHours = newBreakMins / 60;
                        const newTotalHours = formData.breakTimePaid ? newHours + breakHours : newHours;
                        setFormData({ 
                          ...formData, 
                          endTime: newEndTime,
                          unpaidBreakMins: newBreakMins,
                          hours: newHours,
                          totalHours: newTotalHours
                        });
                      }}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                </div>

                {/* Break Minutes and Break Time Paid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Break (minutes)</label>
                    <input
                      type="number"
                      value={formData.unpaidBreakMins}
                      onChange={(e) => {
                        setBreakMinsManuallySet(true); // Mark as manually set
                        const breakMins = parseInt(e.target.value) || 0;
                        const newHours = calculateHours(formData.startTime, formData.endTime, breakMins);
                        // Total Hours: if break is unpaid, use hours (break already deducted)
                        // If break is paid, add break back to get total shift hours
                        const breakHours = breakMins / 60;
                        const newTotalHours = formData.breakTimePaid ? newHours + breakHours : newHours;
                        setFormData({ 
                          ...formData, 
                          unpaidBreakMins: breakMins,
                          hours: newHours,
                          totalHours: newTotalHours
                        });
                      }}
                      min="0"
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Break Time Paid</label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => {
                        const newBreakTimePaid = !formData.breakTimePaid;
                        // Recalculate total hours based on new break paid status
                        const breakHours = formData.unpaidBreakMins / 60;
                        const newTotalHours = newBreakTimePaid ? formData.hours + breakHours : formData.hours;
                        setFormData({ ...formData, breakTimePaid: newBreakTimePaid, totalHours: newTotalHours });
                      }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          formData.breakTimePaid ? 'bg-blue-600' : 'bg-neutral-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            formData.breakTimePaid ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                      <span className="text-sm text-neutral-600">
                        {formData.breakTimePaid ? 'Paid' : 'Unpaid'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Hours and Total Hours (read-only) */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Hours</label>
                    <input
                      type="text"
                      value={recalculatedHours.toFixed(2)}
                      disabled
                      className="w-full px-3 py-2 border rounded bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Total Hours</label>
                    <input
                      type="text"
                      value={recalculatedTotalHours.toFixed(2)}
                      disabled
                      className="w-full px-3 py-2 border rounded bg-gray-50"
                    />
                  </div>
                </div>

                {/* Color */}
                <div>
                  <label className="block text-sm font-medium mb-1">Color</label>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-2">
                      {['#3b82f6', '#ef4444', '#8b5cf6', '#ec4899', '#f97316', '#10b981'].map(color => (
                        <button
                          key={color}
                          onClick={() => setFormData({ ...formData, color })}
                          className={`w-8 h-8 rounded-full border-2 ${
                            formData.color === color ? 'border-neutral-900' : 'border-neutral-300'
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                      <input
                        type="color"
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value, isManualColorOverride: true })}
                        className="w-8 h-8 rounded border border-neutral-300 cursor-pointer"
                      />
                    </div>
                    {formData.color !== formData.originalColor && (
                      <label className="flex items-center gap-2 ml-4">
                        <input
                          type="checkbox"
                          checked={!formData.isManualColorOverride}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, color: formData.originalColor, isManualColorOverride: false });
                            }
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Set to Default</span>
                      </label>
                    )}
                  </div>
                </div>

                {/* End Time Display As */}
                <div>
                  <label className="block text-sm font-medium mb-1">End Time Display As</label>
                  <select
                    value={formData.displayOption}
                    onChange={(e) => setFormData({ ...formData, displayOption: e.target.value as 'time' | 'f' | 'close' })}
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="time">End Time</option>
                    <option value="f">F</option>
                    <option value="close">Close</option>
                  </select>
                </div>

                {/* Position */}
                <div className="relative position-dropdown-container">
                  <label className="block text-sm font-medium mb-1">Position</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowPositionDropdown(!showPositionDropdown)}
                      className="w-full px-3 py-2 border rounded text-left flex items-center justify-between bg-white"
                    >
                      <span className="flex items-center gap-2">
                        {formData.positionId ? (
                          <>
                            <div 
                              className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0" 
                              style={{ backgroundColor: allAvailablePositions.find(p => p.id === formData.positionId)?.color || '#3b82f6' }}
                            />
                            <span>{allAvailablePositions.find(p => p.id === formData.positionId)?.title || 'Select Position'}</span>
                          </>
                        ) : (
                          <span className="text-gray-500">Select Position</span>
                        )}
                      </span>
                      <span className="text-gray-400">▼</span>
                    </button>
                    {showPositionDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        <div
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                          onClick={() => {
                            setFormData({ 
                              ...formData, 
                              positionId: undefined,
                              color: formData.color,
                              originalColor: formData.color
                            });
                            setShowPositionDropdown(false);
                          }}
                        >
                          <span className="text-gray-500">Select Position</span>
                        </div>
                        {allAvailablePositions.map(pos => (
                          <div
                            key={pos.id}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                            onClick={() => {
                              const newColor = colorMode === 'position' && pos.id
                                ? getShiftColorForMode(pos.id)
                                : formData.color;
                              setFormData({ 
                                ...formData, 
                                positionId: pos.id,
                                color: newColor,
                                originalColor: newColor
                              });
                              setShowPositionDropdown(false);
                            }}
                          >
                            <div 
                              className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0" 
                              style={{ backgroundColor: pos.color }}
                            />
                            <span>{pos.title}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border rounded"
                    placeholder="Add notes..."
                  />
                </div>

                {/* Buttons */}
                <div className="flex gap-2 pt-4">
                  <button
                    onClick={() => {
                      setShowShiftForm(false);
                      setEditingShift(null);
                      setBreakMinsManuallySet(false);
                      setShowPositionDropdown(false);
                    }}
                    className="px-4 py-2 border rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  {editingShift.id !== 0 && (
                    <button
                      onClick={() => handleDeleteShift(editingShift.id)}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      <Trash2 className="w-4 h-4 inline mr-1" />
                      Delete
                    </button>
                  )}
                  <button
                    onClick={handleSaveShift}
                    disabled={savingShift}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {savingShift && <Loader2 className="w-4 h-4 animate-spin" />}
                    {savingShift ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Unlock Published Schedule Warning Modal */}
      {showUnlockWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            {/* Header */}
            <div className="flex items-center gap-3 p-6 border-b">
              <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                <Unlock className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-800">Unlock Published Schedule</h2>
            </div>

            {/* Body */}
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Shift is published and locked. Editing the published shift will override timecard data.
              </p>
              
              {/* Warning Box */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4 flex items-start gap-3">
                <div className="w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-bold">!</span>
                </div>
                <p className="text-orange-800 text-sm">
                  Once modified, the schedule will be marked as "Changed" and will need to be republished.
                </p>
              </div>

              <p className="text-gray-700 mb-6">Do you want to unlock for editing?</p>

              {/* Buttons */}
              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleUnlockCancel}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUnlockConfirm}
                  className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700"
                >
                  Yes, Unlock for Editing
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Week Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Select Week to View Records</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Choose a week to display all shifts/records for that period.
                </p>
              </div>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportWeekStart(null);
                  setImportShifts([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 flex-1 overflow-y-auto">
              {/* Week Selector */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Week
                </label>
                <div className="relative">
                  <input
                    type="date"
                    onChange={(e) => {
                      if (e.target.value) {
                        const selectedDate = new Date(e.target.value);
                        const weekStart = startOfWeek(selectedDate, { weekStartsOn });
                        handleWeekSelectChange(weekStart);
                      }
                    }}
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Select a week..."
                  />
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
                {importWeekStart && (
                  <p className="text-sm text-gray-600 mt-2">
                    Selected: {format(importWeekStart, 'MMM d')} - {format(endOfWeek(importWeekStart, { weekStartsOn }), 'MMM d, yyyy')}
                  </p>
                )}
              </div>

              {/* Week Summary */}
              {importWeekStart && importShifts.length > 0 && (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-700">Week Summary</span>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-green-700">
                          {importSummary.totalHours.toFixed(2)} hrs
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {importSummary.recordCount} records
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Shift Records */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">
                      Shift Records ({importSummary.recordCount} total)
                    </h3>
                    <div className="space-y-3">
                      {importShifts.map((shift, index) => {
                        const shiftDate = parseLocalDateTime(shift.startTime);
                        const startTime = format(parseLocalDateTime(shift.startTime), 'HH:mm');
                        const endTime = format(parseLocalDateTime(shift.endTime), 'HH:mm');
                        const totalHours = (shift.paidHours || 0) + (shift.unpaidHours || 0);
                        const employee = employees.find(e => e.id === shift.employeeId);
                        const position = positions.find(p => p.id === shift.positionId);
                        
                        return (
                          <div key={shift.id || index} className="border-b border-gray-200 pb-3 last:border-b-0">
                            <div className="flex items-center justify-between">
                              {/* Left Side - Employee Info */}
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                  <span className="text-blue-700 font-semibold text-sm">
                                    {employee ? `${employee.firstName?.[0] || ''}${employee.lastName?.[0] || ''}` : 'N/A'}
                                  </span>
                                </div>
                                <div>
                                  <div className="font-semibold text-gray-800">
                                    {employee ? `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || 'Unknown Employee' : 'Unknown Employee'}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    {position?.title || 'No Position'}
                                  </div>
                                </div>
                              </div>

                              {/* Right Side - Shift Details */}
                              <div className="text-right">
                                <div className="font-semibold text-gray-800">
                                  {startTime} - {endTime}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {format(shiftDate, 'EEE, d MMM yyyy')}
                                </div>
                                <div className="text-sm text-gray-600">
                                  Hours: {totalHours.toFixed(2)}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

            </div>

            {/* Footer */}
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportWeekStart(null);
                  setImportShifts([]);
                }}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-gray-700"
              >
                Close
              </button>
              {importShifts.length > 0 && (
                <button
                  onClick={() => setShowImportConfirm(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Import {importSummary.recordCount} Records
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Import Confirmation Modal */}
      {showImportConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Confirm Import
              </h3>
              <p className="text-gray-700 mb-6">
                This will replace all records in the current week ({format(weekStart, 'MMM d')} - {format(endOfWeek(weekStart, { weekStartsOn }), 'MMM d, yyyy')}) with {importSummary.recordCount} records from the selected week. This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowImportConfirm(false)}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImportConfirm}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Confirm Import
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sort Employees Modal */}
      {showSortModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-xl font-semibold">Sort Employees</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Use the dropdown to select employees for each position, or drag and drop rows to reorder them.
                </p>
              </div>
              <button
                onClick={() => setShowSortModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Search Bar */}
            <div className="p-4 border-b">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search employees for selection..."
                  className="w-full px-3 py-2 pl-8 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400">🔍</span>
              </div>
            </div>

            {/* Employee List */}
            <div className="flex-1 overflow-y-auto p-4">
              {filteredEmployees.map((employee, index) => {
                const sort = employeeSortOrder.find(o => o.employeeId === employee.id);
                const displayOrder = sort ? sort.sortOrder : index + 1;
                const initials = `${employee.firstName[0]}${employee.lastName[0]}`.toUpperCase();
                
                return (
                  <div
                    key={employee.id}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', employee.id.toString());
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const draggedEmployeeId = parseInt(e.dataTransfer.getData('text/plain'));
                      if (draggedEmployeeId !== employee.id) {
                        const newOrder = [...employeeSortOrder];
                        const draggedIndex = newOrder.findIndex(o => o.employeeId === draggedEmployeeId);
                        const targetIndex = newOrder.findIndex(o => o.employeeId === employee.id);
                        
                        if (draggedIndex !== -1 && targetIndex !== -1) {
                          const [removed] = newOrder.splice(draggedIndex, 1);
                          newOrder.splice(targetIndex, 0, removed);
                          // Reassign sort orders
                          newOrder.forEach((item, idx) => {
                            item.sortOrder = idx + 1;
                          });
                          setEmployeeSortOrder(newOrder);
                        } else {
                          // If dragged item not in sort order, add it
                          if (draggedIndex === -1) {
                            const draggedEmp = filteredEmployees.find(e => e.id === draggedEmployeeId);
                            if (draggedEmp) {
                              newOrder.splice(targetIndex, 0, { employeeId: draggedEmployeeId, sortOrder: targetIndex + 1 });
                              newOrder.forEach((item, idx) => {
                                item.sortOrder = idx + 1;
                              });
                              setEmployeeSortOrder(newOrder);
                            }
                          }
                        }
                      }
                    }}
                    className="flex items-center gap-3 p-3 border rounded-lg mb-2 cursor-move hover:bg-gray-50"
                  >
                    <GripVertical className="w-5 h-5 text-gray-400" />
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold text-sm">
                      {displayOrder}
                    </div>
                    <div className="w-10 h-10 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center font-semibold text-sm">
                      {initials}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">{employee.firstName} {employee.lastName}</div>
                    </div>
                    <span className="text-gray-400">▼</span>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 p-4 border-t">
              <button
                onClick={() => setShowSortModal(false)}
                className="px-4 py-2 border rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (selectedLocationId && selectedScheduleId) {
                    try {
                      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
                      await employeeSortOrderApi.save(selectedLocationId, selectedScheduleId, weekStartStr, employeeSortOrder);
                      setShowSortModal(false);
                    } catch (error) {
                      console.error('Error saving sort order:', error);
                      alert('Failed to save sort order. Please try again.');
                    }
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Save Sort Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cost Breakdown Modal */}
      {showCostBreakdownModal && selectedEmployeeForBreakdown && (() => {
        const payRecord = employeePayRecords[selectedEmployeeForBreakdown.id];
        const formulaInfo = payRecord ? getFormulaExplanation(payRecord) : null;
        const shiftBreakdown = getEmployeeShiftBreakdown(selectedEmployeeForBreakdown.id);
        const totalCost = getEmployeeTotalCost(selectedEmployeeForBreakdown.id);

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl my-8 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  Cost Breakdown - {selectedEmployeeForBreakdown.firstName} {selectedEmployeeForBreakdown.lastName}
                </h3>
                <button
                  onClick={() => {
                    setShowCostBreakdownModal(false);
                    setSelectedEmployeeForBreakdown(null);
                  }}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {!payRecord ? (
                <div className="text-center py-8 text-gray-500">
                  No PayRecord found for this employee.
                </div>
              ) : (
                <div className="space-y-6">
                  {/* PayRecord Details */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-3">PayRecord Details</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Payment Type:</span> {payRecord.paymentType?.name || 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">Payment Frequency:</span> {payRecord.paymentFrequency?.name || 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">Base Rate:</span> €{payRecord.baseRate?.toFixed(2) || '0.00'}
                      </div>
                      <div>
                        <span className="font-medium">Contracted Hours (per week):</span> {payRecord.contractedHours?.toFixed(2) || '0.00'}
                      </div>
                      {payRecord.assignLaborCostBy && (
                        <div>
                          <span className="font-medium">Assign Labor Cost By:</span> {payRecord.assignLaborCostBy.name}
                        </div>
                      )}
                      {payRecord.workingDaysPerWeek && (
                        <div>
                          <span className="font-medium">Working Days per Week:</span> {payRecord.workingDaysPerWeek}
                        </div>
                      )}
                      {payRecord.dailyRate && (
                        <div>
                          <span className="font-medium">Daily Rate:</span> €{payRecord.dailyRate.toFixed(2)}
                        </div>
                      )}
                      {payRecord.weeklyRate && (
                        <div>
                          <span className="font-medium">Weekly Rate:</span> €{payRecord.weeklyRate.toFixed(2)}
                        </div>
                      )}
                      {payRecord.payRecordWorkingDays && payRecord.payRecordWorkingDays.length > 0 && (
                        <div>
                          <span className="font-medium">Preset Days:</span> {payRecord.payRecordWorkingDays.map(prwd => prwd.workingDay?.name).filter(Boolean).join(', ')}
                        </div>
                      )}
                      {payRecord.paymentType?.name === 'Project' && payRecord.projectStartDate && payRecord.projectEndDate && (
                        <>
                          <div>
                            <span className="font-medium">Project Start Date:</span> {new Date(payRecord.projectStartDate).toLocaleDateString()}
                          </div>
                          <div>
                            <span className="font-medium">Project End Date:</span> {new Date(payRecord.projectEndDate).toLocaleDateString()}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Formula Explanation */}
                  {formulaInfo && (
                    <div className="border rounded-lg p-4">
                      <h4 className="font-semibold mb-3">Calculation Formula</h4>
                      <div className="bg-gray-50 p-3 rounded mb-2">
                        <div className="font-mono text-sm font-semibold mb-1">{formulaInfo.formula}</div>
                        <div className="text-sm text-gray-600">{formulaInfo.description}</div>
                      </div>
                      <div className="space-y-1 text-sm">
                        {Object.entries(formulaInfo.values).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="font-medium">{key}:</span>
                            <span>{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Shift Breakdown */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-3">Shift Breakdown for Week</h4>
                    {shiftBreakdown.length === 0 ? (
                      <div className="text-center py-4 text-gray-500">No shifts scheduled for this week.</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Start Time</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">End Time</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Break (mins)</th>
                              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Break Paid</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Hours</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Hourly Rate</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Cost</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {shiftBreakdown.map((item, index) => (
                              <tr key={index}>
                                <td className="px-3 py-2 whitespace-nowrap text-sm">{format(item.date, 'EEE, MMM d')}</td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm">{format(parseLocalDateTime(item.shift.startTime), 'HH:mm')}</td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm">{format(parseLocalDateTime(item.shift.endTime), 'HH:mm')}</td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-right">{item.breakMins}</td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-center">
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    item.breakPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                  }`}>
                                    {item.breakPaid ? 'Paid' : 'Unpaid'}
                                  </span>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-right">{item.shiftHours.toFixed(2)}h</td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-right">€{item.hourlyRate.toFixed(2)}</td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-semibold">€{item.cost.toFixed(2)}</td>
                              </tr>
                            ))}
                            <tr className="bg-gray-50 font-semibold">
                              <td colSpan={7} className="px-3 py-2 text-right text-sm">Total:</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-right">€{totalCost.toFixed(2)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Schedule Breakdown Modal */}
      {showScheduleBreakdownModal && selectedLocationId && (() => {
        // Get all unique schedule IDs from shifts for this location
        const scheduleIdsFromShifts = new Set(
          locationShifts.map(shift => shift.scheduleId).filter(id => id !== undefined && id !== null)
        );

        // Get all schedules that either:
        // 1. Have locationIds including selectedLocationId, OR
        // 2. Have shifts in the locationShifts array
        const locationSchedules = schedules.filter(s => 
          (s.locationIds && s.locationIds.includes(selectedLocationId)) ||
          scheduleIdsFromShifts.has(s.id)
        );

        // Calculate breakdown per schedule/day
        const scheduleBreakdown: Record<number, {
          scheduleName: string;
          hourly: Record<string, number>; // day -> cost
          salary: Record<string, number>; // day -> cost
        }> = {};

        // Initialize breakdown structure for all schedules that have shifts
        // Also include schedules filtered by locationIds to show empty rows if needed
        const allSchedulesToShow = new Set([
          ...locationSchedules.map(s => s.id),
          ...Array.from(scheduleIdsFromShifts)
        ]);

        allSchedulesToShow.forEach(scheduleId => {
          const schedule = schedules.find(s => s.id === scheduleId);
          if (schedule) {
            scheduleBreakdown[scheduleId] = {
              scheduleName: schedule.name,
              hourly: {},
              salary: {},
            };
            weekDays.forEach(day => {
              const dayStr = format(day, 'yyyy-MM-dd');
              scheduleBreakdown[scheduleId].hourly[dayStr] = 0;
              scheduleBreakdown[scheduleId].salary[dayStr] = 0;
            });
          } else {
            // Schedule not found in schedules list, but has shifts - use scheduleId as name
            scheduleBreakdown[scheduleId] = {
              scheduleName: `Schedule ${scheduleId}`,
              hourly: {},
              salary: {},
            };
            weekDays.forEach(day => {
              const dayStr = format(day, 'yyyy-MM-dd');
              scheduleBreakdown[scheduleId].hourly[dayStr] = 0;
              scheduleBreakdown[scheduleId].salary[dayStr] = 0;
            });
          }
        });

        // Helper function to calculate shift cost (similar to getShiftLaborCost but uses payRecord parameter)
        const calculateShiftCost = (shift: Shift, payRecord: PayRecord): number => {
          if (!payRecord) return 0;
          
          const hourlyRate = calculateHourlyRateFromPayRecord(payRecord);
          const shiftHours = getShiftPaidHoursForCost(shift);
          
          return hourlyRate * shiftHours;
        };

        // Calculate costs per shift
        locationShifts.forEach(shift => {
          const dayStr = format(parseISO(shift.startTime), 'yyyy-MM-dd');
          const payRecord = locationPayRecords[shift.employeeId];
          
          if (!payRecord) return;
          
          // Initialize breakdown for this schedule if it doesn't exist
          if (!scheduleBreakdown[shift.scheduleId]) {
            const schedule = schedules.find(s => s.id === shift.scheduleId);
            scheduleBreakdown[shift.scheduleId] = {
              scheduleName: schedule?.name || `Schedule ${shift.scheduleId}`,
              hourly: {},
              salary: {},
            };
            weekDays.forEach(day => {
              const dayStr = format(day, 'yyyy-MM-dd');
              scheduleBreakdown[shift.scheduleId].hourly[dayStr] = 0;
              scheduleBreakdown[shift.scheduleId].salary[dayStr] = 0;
            });
          }
          
          const shiftCost = calculateShiftCost(shift, payRecord);
          const paymentTypeName = payRecord.paymentType?.name || '';
          
          // Categorize as hourly or salary (non-hourly)
          if (paymentTypeName === 'Hourly') {
            scheduleBreakdown[shift.scheduleId].hourly[dayStr] = 
              (scheduleBreakdown[shift.scheduleId].hourly[dayStr] || 0) + shiftCost;
          } else {
            scheduleBreakdown[shift.scheduleId].salary[dayStr] = 
              (scheduleBreakdown[shift.scheduleId].salary[dayStr] || 0) + shiftCost;
          }
        });

        // Calculate totals
        const weeklyTotals = {
          hourly: 0,
          salary: 0,
        };
        
        Object.values(scheduleBreakdown).forEach(schedule => {
          Object.values(schedule.hourly).forEach(cost => weeklyTotals.hourly += cost);
          Object.values(schedule.salary).forEach(cost => weeklyTotals.salary += cost);
        });

        // Schedules in summary order (descending by total cost) - used for table and charts
        const scheduleCostsOrdered = Object.entries(scheduleBreakdown)
          .map(([scheduleIdStr, breakdown]) => {
            const scheduleHourly = Object.values(breakdown.hourly).reduce((s, c) => s + c, 0);
            const scheduleSalary = Object.values(breakdown.salary).reduce((s, c) => s + c, 0);
            const cost = scheduleHourly + scheduleSalary;
            return {
              scheduleId: scheduleIdStr,
              scheduleName: breakdown.scheduleName,
              cost,
              hourly: scheduleHourly,
              salary: scheduleSalary,
              breakdown,
            };
          })
          .filter(({ cost }) => cost > 0)
          .sort((a, b) => b.cost - a.cost);

        // Per-day max for bar scale (so visual bars are comparable across cells)
        const dayMaxByDay: Record<string, number> = {};
        weekDays.forEach(day => {
          const dayStr = format(day, 'yyyy-MM-dd');
          let max = 0;
          scheduleCostsOrdered.forEach(({ breakdown }) => {
            const dayTotal = (breakdown.hourly[dayStr] || 0) + (breakdown.salary[dayStr] || 0);
            if (dayTotal > max) max = dayTotal;
          });
          dayMaxByDay[dayStr] = Math.max(max, 1);
        });

        // Format date range for title
        const dateRangeStr = `${format(weekStart, 'MMM d')} to ${format(weekEnd, 'MMM d')}`;

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 w-full max-w-7xl my-8 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-gray-600" />
                  <h3 className="text-lg font-semibold">
                    Weekly Schedule Breakdown - {dateRangeStr}
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowScheduleBreakdownModal(false);
                    setLocationEmployees([]);
                    setLocationShifts([]);
                    setLocationPayRecords({});
                  }}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {loadingBreakdown ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                  <span className="ml-3 text-gray-600">Loading breakdown data...</span>
                </div>
              ) : (
              <>
              {/* Hidden: original numeric data table (can be re-enabled later) */}
              {false && (
              <div className="overflow-x-auto mb-6" style={{ maxHeight: 'calc(90vh - 300px)', overflowY: 'auto' }}>
                <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border-r border-gray-300">Schedule</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border-r border-gray-300">Type</th>
                      {weekDays.map(day => (
                        <th key={day.toISOString()} className="px-3 py-3 text-center text-xs font-medium text-gray-700 uppercase border-r border-gray-300">
                          {format(day, 'EEE, MMM d')}
                        </th>
                      ))}
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Object.entries(scheduleBreakdown)
                      .sort(([idA], [idB]) => {
                        const nameA = scheduleBreakdown[Number(idA)].scheduleName;
                        const nameB = scheduleBreakdown[Number(idB)].scheduleName;
                        return nameA.localeCompare(nameB);
                      })
                      .map(([scheduleIdStr, breakdown]) => {
                        const scheduleId = Number(scheduleIdStr);
                        const scheduleHourlyTotal = Object.values(breakdown.hourly).reduce((sum, cost) => sum + cost, 0);
                        const scheduleSalaryTotal = Object.values(breakdown.salary).reduce((sum, cost) => sum + cost, 0);
                        const scheduleTotal = scheduleHourlyTotal + scheduleSalaryTotal;
                        if (scheduleTotal === 0) return null;
                        return (
                          <React.Fragment key={scheduleId}>
                            <tr className="bg-gray-50">
                              <td className="px-4 py-2 font-semibold border-r border-gray-300">{breakdown.scheduleName}</td>
                              <td className="px-4 py-2 text-sm border-r border-gray-300">Hourly</td>
                              {weekDays.map(day => {
                                const dayStr = format(day, 'yyyy-MM-dd');
                                const cost = breakdown.hourly[dayStr] || 0;
                                return (
                                  <td key={dayStr} className="px-3 py-2 text-center text-sm border-r border-gray-300">
                                    {cost > 0 ? `€${cost.toFixed(2)}` : '€0.00'}
                                  </td>
                                );
                              })}
                              <td className="px-4 py-2 text-center text-sm font-semibold">€{scheduleHourlyTotal.toFixed(2)}</td>
                            </tr>
                            <tr className="bg-gray-50">
                              <td className="px-4 py-2 border-r border-gray-300"></td>
                              <td className="px-4 py-2 text-sm border-r border-gray-300">Salary</td>
                              {weekDays.map(day => {
                                const dayStr = format(day, 'yyyy-MM-dd');
                                const cost = breakdown.salary[dayStr] || 0;
                                return (
                                  <td key={dayStr} className="px-3 py-2 text-center text-sm border-r border-gray-300">
                                    {cost > 0 ? `€${cost.toFixed(2)}` : '€0.00'}
                                  </td>
                                );
                              })}
                              <td className="px-4 py-2 text-center text-sm font-semibold">€{scheduleSalaryTotal.toFixed(2)}</td>
                            </tr>
                            <tr className="bg-blue-100 border-t-2 border-blue-300">
                              <td className="px-4 py-2 font-semibold border-r border-gray-300">{breakdown.scheduleName}</td>
                              <td className="px-4 py-2 font-semibold border-r border-gray-300">Total</td>
                              {weekDays.map(day => {
                                const dayStr = format(day, 'yyyy-MM-dd');
                                const dayTotal = (breakdown.hourly[dayStr] || 0) + (breakdown.salary[dayStr] || 0);
                                return (
                                  <td key={dayStr} className="px-3 py-2 text-center text-sm font-semibold border-r border-gray-300">
                                    {dayTotal > 0 ? `€${dayTotal.toFixed(2)}` : '€0.00'}
                                  </td>
                                );
                              })}
                              <td className="px-4 py-2 text-center text-sm font-bold text-blue-700">€{scheduleTotal.toFixed(2)}</td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    <tr className="bg-gray-200 font-bold border-t-2 border-gray-400">
                      <td className="px-4 py-3 border-r border-gray-300 font-semibold">Grand Total</td>
                      <td className="px-4 py-3 border-r border-gray-300 font-semibold">Total</td>
                      {weekDays.map(day => {
                        const dayStr = format(day, 'yyyy-MM-dd');
                        const dayHourlyTotal = Object.values(scheduleBreakdown).reduce((sum, schedule) => sum + (schedule.hourly[dayStr] || 0), 0);
                        const daySalaryTotal = Object.values(scheduleBreakdown).reduce((sum, schedule) => sum + (schedule.salary[dayStr] || 0), 0);
                        const dayGrandTotal = dayHourlyTotal + daySalaryTotal;
                        return (
                          <td key={dayStr} className="px-3 py-3 text-center text-sm font-semibold border-r border-gray-300">
                            {dayGrandTotal > 0 ? `€${dayGrandTotal.toFixed(2)}` : '€0.00'}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 text-center text-sm font-semibold">€{(weeklyTotals.hourly + weeklyTotals.salary).toFixed(2)}</td>
                    </tr>
                    <tr className="bg-gray-100">
                      <td className="px-4 py-2 border-r border-gray-300"></td>
                      <td className="px-4 py-2 text-sm font-semibold border-r border-gray-300">Hourly</td>
                      {weekDays.map(day => {
                        const dayStr = format(day, 'yyyy-MM-dd');
                        const dayHourlyTotal = Object.values(scheduleBreakdown).reduce((sum, schedule) => sum + (schedule.hourly[dayStr] || 0), 0);
                        return (
                          <td key={dayStr} className="px-3 py-2 text-center text-sm font-semibold border-r border-gray-300">
                            {dayHourlyTotal > 0 ? `€${dayHourlyTotal.toFixed(2)}` : '€0.00'}
                          </td>
                        );
                      })}
                      <td className="px-4 py-2 text-center text-sm font-semibold">€{weeklyTotals.hourly.toFixed(2)}</td>
                    </tr>
                    <tr className="bg-gray-100">
                      <td className="px-4 py-2 border-r border-gray-300"></td>
                      <td className="px-4 py-2 text-sm font-semibold border-r border-gray-300">Salary</td>
                      {weekDays.map(day => {
                        const dayStr = format(day, 'yyyy-MM-dd');
                        const daySalaryTotal = Object.values(scheduleBreakdown).reduce((sum, schedule) => sum + (schedule.salary[dayStr] || 0), 0);
                        return (
                          <td key={dayStr} className="px-3 py-2 text-center text-sm font-semibold border-r border-gray-300">
                            {daySalaryTotal > 0 ? `€${daySalaryTotal.toFixed(2)}` : '€0.00'}
                          </td>
                        );
                      })}
                      <td className="px-4 py-2 text-center text-sm font-semibold">€{weeklyTotals.salary.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              )}

              {/* New: Hourly / Salary breakup per day by visualization (same order as summary) */}
              <div className="overflow-x-auto mb-6" style={{ maxHeight: 'calc(90vh - 300px)', overflowY: 'auto' }}>
                <div className="flex gap-2 mb-2 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#2563eb]" /> Hourly</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#0d9488]" /> Salary</span>
                </div>
                <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border-r border-gray-300">Schedule</th>
                      {weekDays.map(day => (
                        <th key={day.toISOString()} className="px-2 py-3 text-center text-xs font-medium text-gray-700 uppercase border-r border-gray-300 min-w-[100px]">
                          {format(day, 'EEE, MMM d')}
                        </th>
                      ))}
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase min-w-[80px]">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {scheduleCostsOrdered.length === 0 ? (
                      <tr>
                        <td colSpan={weekDays.length + 2} className="px-4 py-8 text-center text-gray-500">
                          No schedule costs this week.
                        </td>
                      </tr>
                    ) : (
                      scheduleCostsOrdered.map(({ scheduleId, scheduleName, cost, breakdown }) => (
                        <tr key={scheduleId} className="hover:bg-gray-50">
                          <td className="px-4 py-2 font-semibold border-r border-gray-300 align-top pt-3">
                            {scheduleName}
                          </td>
                          {weekDays.map(day => {
                            const dayStr = format(day, 'yyyy-MM-dd');
                            const dayHourly = breakdown.hourly[dayStr] || 0;
                            const daySalary = breakdown.salary[dayStr] || 0;
                            const dayTotal = dayHourly + daySalary;
                            const maxDay = dayMaxByDay[dayStr] || 1;
                            const barWidth = 80;
                            const totalBarW = (dayTotal / maxDay) * barWidth;
                            const hourlyW = dayTotal > 0 ? (dayHourly / dayTotal) * totalBarW : 0;
                            const salaryW = dayTotal > 0 ? (daySalary / dayTotal) * totalBarW : 0;
                            return (
                              <td key={dayStr} className="px-2 py-2 border-r border-gray-300 align-top">
                                <div className="flex flex-col items-center gap-0.5">
                                  <div
                                    className="flex h-6 rounded overflow-hidden bg-gray-100 border border-gray-200"
                                    style={{ width: barWidth + 4, minWidth: barWidth + 4 }}
                                    title={`Hourly: €${dayHourly.toFixed(2)} | Salary: €${daySalary.toFixed(2)}`}
                                  >
                                    {dayHourly > 0 && (
                                      <div
                                        className="h-full bg-[#2563eb] min-w-[2px]"
                                        style={{ width: Math.max(hourlyW, 2) }}
                                      />
                                    )}
                                    {daySalary > 0 && (
                                      <div
                                        className="h-full bg-[#0d9488] min-w-[2px]"
                                        style={{ width: Math.max(salaryW, 2) }}
                                      />
                                    )}
                                  </div>
                                  <span className="text-xs font-medium text-gray-700 tabular-nums">
                                    {dayTotal > 0 ? `€${dayTotal.toFixed(2)}` : '—'}
                                  </span>
                                </div>
                              </td>
                            );
                          })}
                          <td className="px-4 py-2 text-center font-semibold text-gray-800 align-top pt-3">
                            €{cost.toFixed(2)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Cost breakdown: horizontal bar chart (left) + circle chart (right) - Sticky at bottom */}
              {(() => {
                const total = weeklyTotals.hourly + weeklyTotals.salary;
                const hourly = weeklyTotals.hourly;
                const salary = weeklyTotals.salary;
                const scheduleCosts = scheduleCostsOrdered.map(({ scheduleId, scheduleName, cost, hourly, salary }) => ({
                  scheduleId,
                  name: scheduleName,
                  cost,
                  hourly,
                  salary,
                }));
                const maxScheduleCost = Math.max(...scheduleCosts.map(s => s.cost), 1);
                const barChartWidth = 240;
                const barHeight = 22;
                const barGap = 4;
                const labelWidth = 100;
                const size = 220;
                const cx = size / 2;
                const cy = size / 2;
                const outerR = 72;
                const innerR = 44;
                const deg = (n: number) => (n / (total || 1)) * 360;
                const toRad = (d: number) => (d - 90) * (Math.PI / 180);
                const segmentPath = (startDeg: number, endDeg: number) => {
                  const start = toRad(startDeg);
                  const end = toRad(endDeg);
                  const x1 = cx + outerR * Math.cos(start);
                  const y1 = cy + outerR * Math.sin(start);
                  const x2 = cx + outerR * Math.cos(end);
                  const y2 = cy + outerR * Math.sin(end);
                  const x3 = cx + innerR * Math.cos(end);
                  const y3 = cy + innerR * Math.sin(end);
                  const x4 = cx + innerR * Math.cos(start);
                  const y4 = cy + innerR * Math.sin(start);
                  const large = endDeg - startDeg > 180 ? 1 : 0;
                  return `M ${x1} ${y1} A ${outerR} ${outerR} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerR} ${innerR} 0 ${large} 0 ${x4} ${y4} Z`;
                };
                const hasData = total > 0;
                return (
                  <div className="flex flex-wrap items-center justify-center gap-8 mb-6 bg-white pt-4 border-t-2 border-gray-300 sticky bottom-0 z-10">
                    {/* Horizontal bar chart - weekly cost per schedule */}
                    <div className="flex flex-col min-w-[360px]">
                      <div className="text-xs font-semibold text-gray-600 mb-2">Weekly cost by schedule</div>
                      <div className="flex gap-4 mb-1.5 text-xs text-gray-500">
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#2563eb]" /> Hourly</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#0d9488]" /> Salary</span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        {scheduleCosts.length === 0 ? (
                          <div className="text-sm text-gray-400 py-2">No schedule costs this week</div>
                        ) : (
                          scheduleCosts.map(({ scheduleId, name, cost, hourly: schedHourly, salary: schedSalary }) => {
                            const totalBarW = (cost / maxScheduleCost) * barChartWidth;
                            const hourlyBarW = cost > 0 ? (schedHourly / cost) * totalBarW : 0;
                            const salaryBarW = cost > 0 ? (schedSalary / cost) * totalBarW : 0;
                            return (
                              <div key={scheduleId} className="flex items-center gap-2" style={{ height: barHeight + barGap }}>
                                <div className="text-xs text-gray-700 truncate font-medium" style={{ width: labelWidth }} title={name}>
                                  {name}
                                </div>
                                <div className="flex-1 flex items-center gap-2" style={{ maxWidth: barChartWidth + 60 }}>
                                  <div className="h-4 bg-gray-200 rounded overflow-hidden flex" style={{ width: barChartWidth }}>
                                    {schedHourly > 0 && (
                                      <div
                                        className="h-full bg-[#2563eb] transition-all"
                                        style={{ width: Math.max(hourlyBarW, 2) }}
                                        title={`Hourly: €${schedHourly.toFixed(2)}`}
                                      />
                                    )}
                                    {schedSalary > 0 && (
                                      <div
                                        className="h-full bg-[#0d9488] transition-all"
                                        style={{ width: Math.max(salaryBarW, 2) }}
                                        title={`Salary: €${schedSalary.toFixed(2)}`}
                                      />
                                    )}
                                  </div>
                                  <span className="text-xs font-semibold text-gray-700 tabular-nums whitespace-nowrap">
                                    €{cost.toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-center">
                      <svg width={size} height={size} className="flex-shrink-0">
                        {hasData ? (
                          <>
                            {hourly > 0 && (
                              <path
                                d={segmentPath(0, deg(hourly))}
                                fill="#2563eb"
                                stroke="white"
                                strokeWidth={1.5}
                              />
                            )}
                            {salary > 0 && (
                              <path
                                d={segmentPath(deg(hourly), 360)}
                                fill="#0d9488"
                                stroke="white"
                                strokeWidth={1.5}
                              />
                            )}
                          </>
                        ) : (
                          <path
                            d={segmentPath(0, 360)}
                            fill="#e5e7eb"
                            stroke="white"
                            strokeWidth={1.5}
                          />
                        )}
                        <circle cx={cx} cy={cy} r={innerR - 2} fill="white" />
                        <text
                          x={cx}
                          y={cy - 6}
                          textAnchor="middle"
                          className="text-sm font-semibold fill-gray-700"
                        >
                          Total
                        </text>
                        <text
                          x={cx}
                          y={cy + 10}
                          textAnchor="middle"
                          className="text-lg font-bold fill-gray-900"
                        >
                          €{total.toFixed(2)}
                        </text>
                      </svg>
                      <span className="text-xs text-gray-500 mt-1">Weekly cost</span>
                    </div>
                    <div className="flex flex-col gap-3 min-w-[160px]">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-[#2563eb] flex-shrink-0" aria-hidden />
                        <div>
                          <div className="text-sm font-semibold text-gray-800">Hourly Employees</div>
                          <div className="text-sm text-gray-600">€{hourly.toFixed(2)}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-[#0d9488] flex-shrink-0" aria-hidden />
                        <div>
                          <div className="text-sm font-semibold text-gray-800">Salary Employees</div>
                          <div className="text-sm text-gray-600">€{salary.toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Close Button */}
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setShowScheduleBreakdownModal(false);
                    setLocationEmployees([]);
                    setLocationShifts([]);
                    setLocationPayRecords({});
                  }}
                  className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
              </>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ScheduleCalendar;

